import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth, getIsSystemAdmin } from './auth/AuthContext';
import { createLandingResolver } from './Data/Membership/landingResolver';
import { resolveStartPage } from './Data/Membership/defaultCommunityLanding';
import StartPageNotice from './Components/StartPageNotice/StartPageNotice';
import { syncDefaultCommunityViewCache } from './state/defaultCommunityView';
import LoginPage from './auth/LoginPage';
import RegisterPage from './auth/RegisterPage';
import SessionGate from './auth/SessionGate';
import SuspensionOverlay from './Components/SuspensionOverlay/SuspensionOverlay';
import ImpersonationBanner from './Components/ImpersonationBanner/ImpersonationBanner';
import './App.css';
import l4eLogo from './assets/l4e-logo.svg';

// Dev security fixtures — static import for Vite compatibility (replaces CRA require())
// DEV-932: dev-user-switcher source migrated from a static flatfile to
// the API. useApiSecurityData (already imported below) fetches
// security_users from the database (seeded by migration 010); the
// switcher consumes the hook locally.

import { projectsKpi } from './Data';
import { scheduleProgressData } from './Data/scheduleprogressdata';
import { getApprovedScheduleProgressForProject } from './Data/KpiProgress/progressEntries';
import { kpiConfig } from './Data/KpiEngine/kpiConfig';
// Seed data imports removed — mission data comes exclusively from the API.
// import { missionMetricsData } from './Data/KpiEngine/missionMetricsData';
// import { missionTargets } from './Data/KpiEngine/missionTargets';
import { officialReportingCalendar } from './Data/KpiEngine/officialReportingCalendar';

import type { KpiMode } from './kpi-engine';
import { computeKpisForProject } from './kpi-engine';
import { computeScheduleCategoryFromStorage, computeScheduleCategoryFromData, getScheduleInstancesFromStorage } from './kpi-engine/scheduleDataAdapter';
import { computeFinanceCategoryFromStorage, computeFinanceFallbackFromStorage, computeFinanceCategoryFromData, computeFinanceFallbackFromData } from './kpi-engine/budgetDataAdapter';
import type { SubKpiInstance } from './Data/ProjectConfig/projectKpiConfigOverrides';
import {
  hydrateScheduleSeries, hydrateBudgetSeries, hydrateCurveMeta, hydrateKpiConfigs,
  hydrateProgressEntries, hydrateWorkflows,
  hydrateProjectConfigs, hydrateProjectAcl, hydrateCommunityRoles,
  hydrateCommunityMemberships, hydrateCommunityConfigs,
  hydrateCollectionProjectLinks, hydrateProjectStumps,
} from './Data/store';
import { getScheduleProgressOverrideSeriesForInstance } from './Data/ScheduleProgress/scheduleProgressOverrides';
import { getProjectKpiConfigOverride } from './Data/ProjectConfig/projectKpiConfigOverrides';
// DEV-851: data reliability engine v2 wiring + extracted helper utils.
import { isProjectCompleted } from './Data/ProjectConfig/isProjectCompleted';
import { isProjectActive } from './Data/ProjectConfig/isProjectActive';
import { resolveReportingCadence, toEngineCadenceInput } from './Data/ProjectConfig/resolveReportingCadence';
import { computeDataReliability, DEFAULT_DATA_RELIABILITY_WEIGHTS } from './kpi-engine/dataReliability';
import type { DataReliabilityInputs } from './kpi-engine/dataReliability';
import {
  buildExpectedInstancesForProject,
  buildInstanceLabelsForProject,
  buildLatestIndices,
  buildAllDatesIndices,
  buildBaselineDatesIndices,
  buildLagIndices,
  buildProgressEntriesByProject,
} from './Data/DataReliability/perInstanceState';
import { todayIso as todayIsoLocal } from './utils/localDate';
import { computeSummaryKpiSignal } from './kpi-engine/summary';
import { useIdleDetector } from './hooks/useIdleDetector';

import { mergeProjectsWithOverrides, refreshCommunityMaps, rederiveCommunityFields, getProjectOverride } from './Data/ProjectConfig/projectConfigOverrides';
import { hydrateTaxonomyFromApi } from './Data/ProjectConfig/projectStandardOptions';
import { getTaxonomyNodes } from './api/projectTaxonomy';
import { hydrateEmbedAllowlist } from './Components/ProjectContentSections/embedAllowlist';
import { hydrateSecurityAdapter } from './security';
import { useV2ProjectRoles, v2CanViewProject } from './security/v2/useV2ProjectRoles';
import { getAllProjectsSeedPlusAdded } from './Data/ProjectConfig';
import { useApiProjects } from './api/useApiProjects';
import { useApiScheduleData } from './api/useApiScheduleData';
import { useApiKpiConfigs } from './api/useApiKpiConfigs';
import { useApiMissionData } from './api/useApiMissionData';
import { useApiCommunityData } from './api/useApiCommunityData';
import { useApiSecurityData } from './api/useApiSecurityData';
import { useApiBudgetData } from './api/useApiBudgetData';
import type { ScheduleSeriesPointResponse } from './api/scheduleSeriesPoints';
import type { BudgetSeriesPointResponse } from './api/budgetSeriesPoints';
import { canEditProjectContent } from './security/securityAdapter';
// DEV-839: bulk HSE incidents fetch — used to attach `hse_bundle` to every
// project row so KpiGridTable applies the HSE floor cap with one round trip
// instead of N. The displayKpiState helper degrades cleanly when the bundle
// is absent, so this is the only place the data path needs to land.
import { useHseIncidentsBulk } from './api/hseIncidents';
import { computeHseBundle } from './kpi-engine/hse';
import type { IncidentCountConfig } from './kpi-engine/missionTypes/incidentCount';
import { useApiCurveMeta } from './api/useApiCurveMeta';
import { useApiKpiProgress } from './api/useApiKpiProgress';
import { useApiWorkflows } from './api/useApiWorkflows';
import { useMediaStats } from './api/mediaLinks';
import { API_BASE_URL, getCurrentUserGuid, setCurrentUserGuid } from './api/client';
import { useApiOnboarding } from './api/useApiOnboarding';
import ContributorWelcomeModal from './Components/ContributorWelcomeModal/ContributorWelcomeModal';
import WelcomeScreenModal from './Components/WelcomeScreenModal/WelcomeScreenModal';
import { hasBlueRing } from './Data/Onboarding/ringStatus';
import FeatureGate from './Components/FeatureGate/FeatureGate';
import type { TierKey } from './Components/LeftNav/LeftNav';
import { connectKpiHub, disconnectKpiHub, idleDisconnect, reconnectAfterIdle } from './api/signalrConnection';
import { connectChatHub, disconnectChatHub, idleDisconnectChat, reconnectChatAfterIdle, onChatEvent, ChatHubEvents, joinConversationGroup } from './api/chatConnection';
import { fetchConversations as fetchChatConversations, fetchPendingRequests as fetchChatPendingRequests } from './api/chat';
import { playNotificationSound } from './notifications/notificationSound';
import { useActionItemComposer } from './hooks/useActionItemComposer';
import { useActionItemSeenStore, isUnseenActionItem } from './state/actionItemSeenStore';
import { startCrossTabListener, closeCrossTabChannel } from './api/crossTabSync';
// Query cache invalidation is handled by signalrConnection.ts — no direct usage in App.tsx
import ApiStatusModal from './Components/ApiStatusModal/ApiStatusModal';
import type { ServiceStatus } from './Components/ApiStatusModal/ApiStatusModal';

import LeftNav from './Components/LeftNav/LeftNav';
import GearIcon from './Components/Icons/GearIcon';
import ChatTray from './Components/Chat/ChatTray';
import chatStyles from './Components/Chat/Chat.module.css';
// GeographyAdminModal moved to AdminGeographies page (/admin/geo)
import CommunitiesPage from './Pages/Communities/CommunitiesPage';
import CommunitiesCatalogPage from './Pages/DiscoverCatalog/CommunitiesCatalogPage';
import GeoTreeRail from './Components/Rail/GeoTreeRail';
import CommunitySwitcherRail from './Components/Rail/CommunitySwitcherRail';
import MembershipRolePage from './Pages/Memberships/MembershipRolePage';
import MembershipsHomePage from './Pages/Memberships/MembershipsHomePage';
import ResourceRegistryRail from './Components/Rail/ResourceRegistryRail';
import CommunityDetailPage from './Pages/Communities/CommunityDetailPage';
import CommunityProjectsPage from './Pages/Communities/CommunityProjectsPage';
import WorkspaceDetailPage from './Pages/Workspaces/WorkspaceDetailPage';
import WorkspaceListPage from './Pages/Workspaces/WorkspaceListPage';
import ContentApprovalsPage from './Pages/ContentApprovals/ContentApprovalsPage';
import MyProjectsPage from './Pages/MyProjects/MyProjectsPage';
import MyProjectsCardsPage from './Pages/MyProjects/MyProjectsCardsPage';
/* MyImpactPage is no longer routed directly — DEV-1458 (§7.7): the section
   home (MembershipsHomePage) owns both /memberships and /memberships/impact
   and renders it post-reveal. */
import ProjectDetailPage from './Pages/ProjectDetail/ProjectDetailPage';
// DEV-901 stage 3a: shared /projects/:id route dispatches to Mini or Full
// page based on the GUID's project type (Skip's Q1 decision 2026-05-19).
import ProjectDispatcher from './Pages/ProjectDispatcher/ProjectDispatcher';
import ComingSoonPage from './Pages/ComingSoon/ComingSoonPage';
import MyProblemsPage from './Pages/Problems/MyProblemsPage';
import PortfolioPage from './Pages/Portfolio/PortfolioPage';
import ChroniclesPage from './Pages/Chronicles/ChroniclesPage';
import KnowledgePage from './Pages/Knowledge/KnowledgePage';
import KnowledgeArticlePage from './Pages/Knowledge/KnowledgeArticlePage';
import ContentPage from './Pages/Content/ContentPage';
import ChronicleDetailPage from './Pages/Chronicles/ChronicleDetailPage';
import ResourcesPage from './Pages/Resources/ResourcesPage';
import VerificationPage from './Pages/Verification/VerificationPage';
import HeroDesignExplorer from './Pages/UserProfile/HeroDesignExplorer';
import MessagesPage from './Pages/Messages/MessagesPage';
import NotificationBell from './Components/NotificationBell/NotificationBell';
import AvatarMenu from './Components/AvatarMenu/AvatarMenu';
import { mediaSrc } from './api/mediaUrl';
import { AccountSettingsProvider, useAccountSettings } from './Components/AccountSettings/AccountSettingsContext';
import AccountSettingsModal from './Components/AccountSettings/AccountSettingsModal';
import GlobalSearch from './Components/GlobalSearch/GlobalSearch';
import { useSearchBarMode } from './state/searchBarPreference';
import { flushClientStateForIdentitySwitch } from './auth/identityFlush';
// DEV-1482 — fail-closed nav gating + per-user cached seed (no tier flash).
import { ALL_FEATURE_FLAGS } from './Data/Onboarding/archetypeConfig';
import type { FeatureFlag } from './Data/Onboarding/archetypeConfig';
import {
  readCachedNavFeatures,
  writeCachedNavFeatures,
  NAV_FEATURES_FAIL_CLOSED,
} from './Data/Onboarding/navFeatureCache';
import { useApiMiniProjects } from './api/useApiMiniProjects';
// DEV-1116. Reuse the Mini-Project Progress tab's KPI status computation
// so dot colors in the portfolio grid agree with what the Progress tab
// shows. Phase 1a wired lifecycle short-circuits + DR. Phase 1b (below)
// adds bulk per-MP latest-status + per-goal latestAssessment via the
// new /api/mini-projects/portfolio-signals endpoint, lighting up
// Schedule + Mission for active MPs too.
import { computeMiniProjectKpiStatus, computeMpKpiTrends, type KpiColor, type KpiTrend } from './Pages/MiniProjectDetail/computeKpiStatus';
import { projectDurationDays } from './Data/MiniProjectConfig/scheduleThresholds';
import { miniLifecycleToProjectStatus } from './Data/MiniProjectConfig/miniProjectLifecycle';
import { useApiMiniProjectPortfolioSignals } from './api/useApiMiniProjectPortfolioSignals';

// DEV-1116 / Task #207. Convert the KpiColor strings from
// computeMiniProjectKpiStatus into the numeric color codes the
// portfolio grid expects on `*_indicator_color` fields. The FP engine
// emits this same domain (see Data/displayKpiState.ts IndicatorColorCode):
//   0 = grey / no data        3 = green
//   1 = red                   blue is rendered via a separate
//   2 = yellow                completed-flag override, NOT a 4 code.
//
// CRITICAL — wire format is only 0–3. `displayKpiState.normalizeColorCode`
// returns null for any value outside that range, so emitting 4 for blue
// silently routes completed MPs through the no-data (grey) path. Blue is
// rendered by the helpers via `item._isCompletedProject` (DR) and
// `item.project.project_status === 'Completed'` (Mission/Summary/etc.).
// We set both on the MP stub below, so blue lifts itself out via that
// path regardless of the value we put here. Mapping blue → 3 keeps the
// underlying signal honest if the completed flag ever fails to fire.
function mpColorToCode(c: KpiColor): number {
  switch (c) {
    case 'red': return 1;
    case 'yellow': return 2;
    case 'green': return 3;
    case 'blue': return 3;
    case 'grey':
    default: return 0;
  }
}

// Bug A (portfolio trend arrows on MP rows). Convert a KpiTrend from
// computeMpKpiTrends into the integer the grid's snapshotFromRow reads
// off `*_indicator_trend` via num(). Same encoding the FP kpi-engine
// emits on indicator_trend_value: 1 = up, -1 = down, 0 = flat/no-data
// (circle). null (first status / grey side) collapses to 0 → circle.
function mpTrendToCode(t: KpiTrend): number {
  return t === 'up' ? 1 : t === 'down' ? -1 : 0;
}
import { startNotificationBridge } from './notifications/notificationBridge';
import { startPresenceBridge } from './presence/presenceBridge';
import { usePresenceReporter } from './presence/usePresenceReporter';
import { startHealthMonitor, stopHealthMonitor, useApiHealth } from './api/apiHealthService';
import UserProfilePage from './Pages/UserProfile/UserProfilePage';
import FeedPage from './Pages/Feed/FeedPage';
// DEV-947 — rendered by CommunitySlugRoute when a slug URL fails to resolve.
import CommunityNotFound from './Pages/CommunityNotFound/CommunityNotFound';
import DiscoverGlobe from './Components/DiscoverGlobe/DiscoverGlobe';
import LandingPage from './Pages/Landing/LandingPage';
import InviteLandingPage from './Pages/Onboarding/InviteLandingPage';
import AdminShell from './Pages/Admin/AdminShell';
import AdminDashboard from './Pages/Admin/AdminDashboard';
import AdminLandingContent from './Pages/Admin/AdminLandingContent';
import AdminPlaceholder from './Pages/Admin/AdminPlaceholder';
import AdminApiMonitor from './Pages/Admin/AdminApiMonitor';
import AdminUserManagement from './Pages/Admin/AdminUserManagement';
import AdminPlatformSettings from './Pages/Admin/AdminPlatformSettings';
import AdminThemes from './Pages/Admin/AdminThemes';
import AdminThemeDesigner from './Pages/Admin/AdminThemeDesigner';
import AdminLocalization from './Pages/Admin/AdminLocalization';
import AdminGeographies from './Pages/Admin/AdminGeographies';
import AdminRoleRhythms from './Pages/Admin/AdminRoleRhythms';  // DEV-1157 Phase 2b
import AdminNewsSettings from './Pages/Admin/AdminNewsSettings';
import AdminOnboarding from './Pages/Admin/AdminOnboarding';
import AdminImpactActivities from './Pages/Admin/AdminImpactActivities';
import AdminAccessControl from './Pages/Admin/AdminAccessControl';
import AdminEmbedAllowlist from './Pages/Admin/AdminEmbedAllowlist';
import AdminPillars from './Pages/Admin/AdminPillars';
import AdminContentCleanup from './Pages/Admin/AdminContentCleanup';
import AdminWorkspaceCleanup from './Pages/Admin/AdminWorkspaceCleanup';
import AdminAuditLog from './Pages/Admin/AdminAuditLog';
import AdminModeration from './Pages/Admin/AdminModeration';
import { ProjectSelectionProvider } from './state/ProjectSelectionContext';
import { FocusProvider } from './state/FocusContext';
import ImportProjectsModal from './Components/ImportProjectsModal/ImportProjectsModal';
import ProjectStepper from './Components/ProjectStepper/ProjectStepper';
import { updateGlobePasscode } from './api/systemSettings';

/* ------------------------------------------------------------------ */
/*  Brand text — context-aware "Life4{Community}" in the topbar        */
/* ------------------------------------------------------------------ */

/**
 * DEV-985 — Resolve any community-pointing pathname to its entity.
 *
 * Handles all three URL shapes the app routes:
 *   /communities/{guid}    — legacy GUID URL
 *   /communities/{slug}    — single-segment slug under the legacy path
 *   /{slug}/.../{slug}     — variable-depth slug URL via CommunitySlugRoute
 *                            catch-alls (DEV-947)
 *
 * For multi-segment slug URLs, walks the child→parent chain in
 * `relationships` to disambiguate slug collisions at different depths
 * (e.g. multiple "ma" slugs in different countries). geo_planet (Earth)
 * is transparent — same convention as backend `IsUrlTopLevelAsync` and
 * frontend `communityUrlFor`.
 *
 * Returns null when the pathname doesn't resolve to a community entity.
 */
function resolveCommunityFromPath(
  pathname: string,
  entities?: Record<string, any>[],
  relationships?: Record<string, any>[],
): Record<string, any> | null {
  if (!entities || entities.length === 0) return null;

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  // ── In-section community view: /memberships/community/:guid ──
  // DEV-1458 (§7.7 amended, Skip 2026-07-13): the My Impact rail's membership
  // rows open the community IN THE MAIN PANE, rail intact — the same grammar
  // /memberships/role/:key already uses for roles and projects. It gets its
  // OWN URL rather than re-rendering /communities/:id differently by entry
  // (chrome must never depend on navigation history — §3, §7.7 ruling 2), so
  // the in-section view survives refresh, bookmark, and back.
  //
  // It resolves to the same entity as the standalone page, which is the point:
  // BrandText ("Life4Boston") and PageTitle then behave identically on both.
  if (
    segments[0].toLowerCase() === 'memberships' &&
    segments[1]?.toLowerCase() === 'community' &&
    segments.length >= 3
  ) {
    const g = segments[2];
    return (
      entities.find(
        (e) => String(e.entity_guid || '').toUpperCase() === g.toUpperCase(),
      ) || null
    );
  }

  // ── Legacy /communities/:id path ───────────────────────────
  if (segments[0].toLowerCase() === 'communities' && segments.length >= 2) {
    const id = segments[1];
    const byGuid = entities.find(
      (e) => String(e.entity_guid || '').toUpperCase() === id.toUpperCase()
    );
    if (byGuid) return byGuid;
    const bySlug = entities.find(
      (e) => String(e.slug || '').toLowerCase() === id.toLowerCase()
    );
    return bySlug || null;
  }

  // ── Slug catch-all path ────────────────────────────────────
  const slugMap = new Map<string, Record<string, any>[]>();
  for (const e of entities) {
    const slug = String(e.slug || '').toLowerCase();
    if (slug) {
      const list = slugMap.get(slug) || [];
      list.push(e);
      slugMap.set(slug, list);
    }
  }
  const parentMap = new Map<string, string>();
  for (const r of relationships || []) {
    const child = String(r.child_entity_guid || '').toUpperCase();
    const parent = String(r.parent_entity_guid || '').toUpperCase();
    if (child && parent) parentMap.set(child, parent);
  }
  const geoPlanetGuids = new Set<string>();
  for (const e of entities) {
    if (String(e.entity_type || '').toLowerCase() === 'geo_planet') {
      geoPlanetGuids.add(String(e.entity_guid || '').toUpperCase());
    }
  }

  let current: Record<string, any> | null = null;
  for (let i = 0; i < segments.length; i++) {
    const candidates = slugMap.get(segments[i].toLowerCase());
    if (!candidates || candidates.length === 0) return null;

    if (i === 0) {
      // Root segment: parent must be absent or geo_planet (transparent).
      current = candidates.find((c) => {
        const parentGuid = parentMap.get(String(c.entity_guid || '').toUpperCase());
        return !parentGuid || geoPlanetGuids.has(parentGuid);
      }) || null;
    } else {
      const currentGuid = String(current?.entity_guid || '').toUpperCase();
      current = candidates.find(
        (c) => parentMap.get(String(c.entity_guid || '').toUpperCase()) === currentGuid
      ) || null;
    }

    if (!current) return null;
  }

  return current;
}

/** Returns the resolved entity only when it's geographic and has a name. */
function findActiveGeoCommunity(
  pathname: string,
  entities?: Record<string, any>[],
  relationships?: Record<string, any>[],
): Record<string, any> | null {
  const entity = resolveCommunityFromPath(pathname, entities, relationships);
  if (
    entity &&
    String(entity.entity_type || '').toLowerCase().startsWith('geo_') &&
    entity.name
  ) {
    return entity;
  }
  return null;
}

function BrandText({
  entities,
  relationships,
}: {
  entities?: Record<string, any>[];
  relationships?: Record<string, any>[];
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const geoEntity = findActiveGeoCommunity(location.pathname, entities, relationships);

  if (geoEntity) {
    return (
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
        onClick={() => navigate('/communities')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/communities'); }}
        title="Back to communities"
      >
        <em>Life</em>4{geoEntity.name}
      </span>
    );
  }
  return <><em>Life</em>4Earth</>;
}

/* ------------------------------------------------------------------ */
/*  Page title helper — reads route location                           */
/* ------------------------------------------------------------------ */

function PageTitle({
  entities,
  relationships,
}: {
  entities?: Record<string, any>[];
  relationships?: Record<string, any>[];
}) {
  const location = useLocation();
  const { t } = useTranslation();
  const path = location.pathname;

  if (path === '/welcome') return <>{t('pageTitle.welcome', 'Welcome')}</>;
  if (path.startsWith('/projects/')) return <>{t('pageTitle.projectDetail')}</>;
  if (path.startsWith('/communities/')) {
    // Hide the "Community" label when a geo community is selected —
    // the brand text already shows "Life4{CommunityName}".
    const geoEntity = findActiveGeoCommunity(path, entities, relationships);
    if (geoEntity) return null;
    return <>{t('pageTitle.community')}</>;
  }
  if (path === '/communities') return <>{t('pageTitle.communities')}</>;
  if (path === '/actions' || path === '/approvals') return <>{t('pageTitle.actionCenter', 'Action Center')}</>;
  // ── DEV-1447 — Discover: the hub and its pillar catalogs ──
  //
  // Order matters: the CATALOG branches must come before the bare `/discover`
  // branch, or a prefix match would swallow them.
  //
  // Each catalog names ITSELF rather than inheriting the hub's label — the
  // topbar says WHERE YOU ARE, and the globe is not a catalog.
  //
  // "Communities Catalog", NOT "Communities": `pageTitle.communities` is already
  // taken by /communities (the user's own memberships). Two different surfaces
  // sharing one topbar label is a real ambiguity, not a cosmetic one. Add a line
  // per catalog as each ships.
  if (path.startsWith('/discover/communities')) {
    return <>{t('pageTitle.communitiesCatalog', 'Communities Catalog')}</>;
  }
  // The hub. "Discover" — not "Discover Now", which is the globe's own hero
  // title; the topbar names the SECTION and should match the LeftNav item
  // (Skip, 2026-07-13).
  if (path === '/discover' || path.startsWith('/discover/')) {
    return <>{t('pageTitle.discover', 'Discover')}</>;
  }
  if (path === '/tv') return <>{t('pageTitle.tv')}</>;
  if (path === '/engagements' || path.startsWith('/engagements/')) return <>{t('pageTitle.engagements', 'Engagements')}</>;
  if (path === '/data') return <>{t('pageTitle.data', 'Data')}</>;
  if (path === '/my-projects' || path.startsWith('/my-projects/')) return <>{t('pageTitle.myProjects')}</>;
  if (path === '/problems') return <>{t('pageTitle.problems', 'Problems')}</>;
  if (path === '/solutions') return <>{t('pageTitle.solutions', 'Solutions')}</>;
  if (path === '/content' || path.startsWith('/content/')) return <>{t('pageTitle.content', 'Content')}</>;
  if (path === '/knowledge') return <>{t('pageTitle.knowledge', 'Knowledge')}</>;
  if (path === '/portfolio') return <>{t('pageTitle.portfolio', 'Portfolio')}</>;
  if (path === '/governance') return <>{t('pageTitle.governance', 'Governance')}</>;
  if (path === '/feed') return <>{t('pageTitle.feed')}</>;
  if (path === '/bookmarks') return <>{t('pageTitle.bookmarks')}</>;
  if (path === '/owned-content') return <>{t('pageTitle.ownedContent')}</>;
  if (path === '/profile' || path.startsWith('/profile/')) return <>{t('pageTitle.userProfile')}</>;
  if (path === '/messages' || path.startsWith('/messages/')) return <>{t('pageTitle.messages', 'Messages')}</>;
  if (path === '/resources') return <>{t('pageTitle.resources', 'Resources')}</>;
  if (path === '/chronicles' || path.startsWith('/chronicles/')) return <>{t('pageTitle.chronicles', 'Chronicles')}</>;
  if (path === '/verification' || path.startsWith('/verification')) return <>{t('pageTitle.verification', 'Verification')}</>;

  // DEV-985 — slug-URL catch-all. Must come after all known app routes so
  // we don't shadow them, but before the "My Impact" fallback. A pathname
  // that resolves to a community entity gets the same label treatment as
  // the legacy /communities/:id branch: hide for geo (brand text already
  // shows the name), show "Community" for non-geo.
  const slugEntity = resolveCommunityFromPath(path, entities, relationships);
  if (slugEntity) {
    if (String(slugEntity.entity_type || '').toLowerCase().startsWith('geo_')) return null;
    return <>{t('pageTitle.community')}</>;
  }

  return <>{t('pageTitle.myImpact', 'My Impact')}</>;
}

/* ------------------------------------------------------------------ */
/*  Route Gate — renders public & admin pages OUTSIDE the app shell    */
/* ------------------------------------------------------------------ */

// Public route paths — must stay aligned with PUBLIC_PATHS in src/auth/SessionGate.tsx.
// Both lists are checked independently (SessionGate decides whether to gate render;
// RouteGate decides which Routes block to render). Drift between the two would
// leave one path either ungated (security gap) or unreachable (404 in main app).
// '/invite' → the tokenized invitation-accept landing (/invite/:token),
// reachable pre-auth (DEV-1307 / onboarding Slice C). Kept aligned with
// PUBLIC_PATHS in src/auth/SessionGate.tsx.
const PUBLIC_PATHS = ['/welcome', '/login', '/register', '/reset-password', '/invite'];

function AdminAccessDenied() {
  const navigate = useNavigate();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#f4f5f7', fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', margin: '0 0 8px' }}>Access Denied</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 24px' }}>
          System administrator privileges are required to access the admin console.
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '10px 24px', fontSize: 14, fontWeight: 600,
            color: '#fff', background: '#45BB47', border: 'none',
            borderRadius: 8, cursor: 'pointer',
          }}
        >
          Back to App
        </button>
      </div>
    </div>
  );
}

function RouteGate({ children, isSystemAdmin, securityLoaded, adminServices, adminOnRefresh, adminUsers, adminCommunityEntities, currentAdminGuid, onUsersChanged }: {
  children: React.ReactNode;
  isSystemAdmin: boolean;
  /** true once security data has loaded (or failed), false while still loading */
  securityLoaded: boolean;
  adminServices: ServiceStatus[];
  adminOnRefresh: () => void;
  adminUsers: any[];
  /** DEV-1470: refetch the security data in place after an admin mutates a user. */
  onUsersChanged: () => void;
  adminCommunityEntities: any[];
  /** GUID of the currently logged-in admin — used to prevent self-suspension */
  currentAdminGuid: string;
}) {
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  const isAdmin = location.pathname.startsWith('/admin');

  if (isPublic) {
    return (
      <Routes>
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/invite/:inviteCode" element={<InviteLandingPage />} />
      </Routes>
    );
  }

  if (isAdmin) {
    // Deny access ONLY if security data has loaded and user is NOT admin.
    // If security data hasn't loaded (API down), allow through in degraded mode.
    const degradedMode = !securityLoaded;
    if (securityLoaded && !isSystemAdmin) {
      return (
        <Routes>
          <Route path="/admin/*" element={<AdminAccessDenied />} />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="/admin" element={<AdminShell degradedMode={degradedMode} />}>
          <Route index element={<AdminDashboard />} />
          <Route path="landing" element={<AdminLandingContent />} />
          <Route path="users" element={
            <AdminUserManagement
              users={adminUsers}
              communityEntities={adminCommunityEntities}
              currentAdminGuid={currentAdminGuid}
              onUsersChanged={onUsersChanged}
            />
          } />
          <Route path="geo" element={<AdminGeographies />} />
          <Route path="platform" element={<AdminPlatformSettings />} />
          <Route path="themes" element={<AdminThemes />} />
          <Route path="designer" element={<AdminThemeDesigner />} />
          <Route path="i18n" element={<AdminLocalization />} />
          <Route path="news" element={<AdminNewsSettings />} />
          <Route path="onboarding" element={<AdminOnboarding />} />
          {/* DEV-1480 — Impact Activity Catalog (sysadmin; English-only) */}
          <Route path="activities" element={<AdminImpactActivities />} />
          <Route path="features" element={
            <AdminPlaceholder
              title="Feature Flags"
              description="Toggle platform features on/off globally or per community"
              features={[
                'Global feature toggles (on/off)',
                'Per-community overrides',
                'Feature rollout percentages',
                'Environment-specific flags (dev/staging/prod)',
              ]}
            />
          } />
          <Route path="api" element={
            <AdminApiMonitor
              services={adminServices}
              onRefresh={adminOnRefresh}
            />
          } />
          <Route path="access" element={<AdminAccessControl />} />
          <Route path="embeds" element={<AdminEmbedAllowlist />} />
          <Route path="pillars" element={<AdminPillars />} />
          <Route path="content" element={<AdminContentCleanup />} />
          <Route path="workspaces" element={<AdminWorkspaceCleanup />} />
          <Route path="audit" element={<AdminAuditLog />} />
          <Route path="moderation" element={<AdminModeration />} />
          {/* DEV-1157 Phase 2b — Platform Rhythm Catalog (sysadmin authoring) */}
          <Route path="rhythms" element={<AdminRoleRhythms />} />
        </Route>
      </Routes>
    );
  }

  return <>{children}</>;
}

/* ------------------------------------------------------------------ */
/*  Route wrappers — bridge React Router params → existing components  */
/* ------------------------------------------------------------------ */

function CommunitiesPageRoute({ entities, relationships, currentUserGuid }: { entities?: Record<string, any>[]; relationships?: Record<string, any>[]; currentUserGuid: string }) {
  const navigate = useNavigate();
  // DEV-947: build maps once for the click handler so each click can compute
  // the slug URL instead of emitting the GUID URL and waiting for the
  // CommunityDetailRoute auto-redirect to fix it. Eliminates the URL-bar
  // flash for the highest-traffic click path.
  //
  // DEV-947 followup: data here is snake_case (entity_guid / parent_entity_guid /
  // child_entity_guid / entity_type / relationship_type) — that's what the
  // hydrated apiCommunityEntities/Relationships shape uses, see
  // CommunitiesPage.tsx types. Normalize to the camelCase shape that
  // communityUrlFor/walkAncestorChain expect. Defensively also accept
  // pre-normalized camelCase in case callers ever pass that shape.
  const { entityByGuid, parentMap } = React.useMemo(() => {
    const ebg = new Map<string, any>();
    const pm = new Map<string, string>();
    for (const e of entities ?? []) {
      const guid = e?.entity_guid ?? e?.entityGuid;
      if (!guid) continue;
      ebg.set(String(guid).toUpperCase(), {
        entityGuid: guid,
        slug: e?.slug ?? null,
        entityType: e?.entity_type ?? e?.entityType ?? null,
      });
    }
    for (const r of relationships ?? []) {
      const child = r?.child_entity_guid ?? r?.childEntityGuid;
      const parent = r?.parent_entity_guid ?? r?.parentEntityGuid;
      const rt = r?.relationship_type ?? r?.relationshipType;
      if (child && parent && (rt === 'parent_of' || rt === 'contains')) {
        pm.set(String(child).toUpperCase(), String(parent).toUpperCase());
      }
    }
    return { entityByGuid: ebg, parentMap: pm };
  }, [entities, relationships]);

  const handleOpen = React.useCallback(
    async (guid: string) => {
      const entity = entityByGuid.get(String(guid).toUpperCase());
      if (!entity) {
        navigate(`/communities/${guid}`);
        return;
      }
      const { communityUrlFor, walkAncestorChain } = await import(
        './api/communityMetadata'
      );
      const ancestors = walkAncestorChain(entity, parentMap, entityByGuid);
      navigate(communityUrlFor(entity, ancestors));
    },
    [entityByGuid, parentMap, navigate],
  );

  return (
    <CommunitiesPage
      entities={entities as any}
      relationships={relationships as any}
      currentUserGuid={currentUserGuid}
      onOpenCommunity={handleOpen}
    />
  );
}

/**
 * InSectionCommunityRoute — /memberships/community/:id (DEV-1458, §7.7 as
 * amended by Skip 2026-07-13).
 *
 * The community rendered INSIDE My Impact, with the rail beside it, so a member
 * can switch quickly between their places and organizations — the behavior the
 * Projects/Roles lenses already have via /memberships/role/:key.
 *
 * Deliberately NOT CommunityDetailRoute: that wrapper carries the DEV-947
 * effect that rewrites a GUID URL to the canonical slug URL, which would bounce
 * the user straight out of the section. Here the GUID URL IS the canonical
 * form — the mirror's address for the community, distinct from the world's
 * slug address. CommunityDetailPage itself is untouched and shared by both.
 */
function InSectionCommunityRoute({ currentUserGuid }: { currentUserGuid: string }) {
  const { id } = useParams<{ id: string }>();
  return (
    <CommunityDetailPage communityGuid={id || ''} currentUserGuid={currentUserGuid} />
  );
}

function CommunityDetailRoute({
  currentUserGuid,
  entities,
  relationships,
}: {
  currentUserGuid: string;
  entities?: any[];
  relationships?: any[];
}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // DEV-947: when arriving at the GUID URL but the entity has a complete
  // slug chain, replace the URL with the slug version. Catches bookmarks
  // of legacy GUID URLs and any internal nav site that didn't migrate to
  // communityUrlFor. The replace mode keeps the GUID URL out of history.
  //
  // DEV-947 followup: data here is snake_case (entity_guid / entity_type /
  // child/parent_entity_guid / relationship_type). Normalize to the
  // camelCase shape that communityUrlFor / walkAncestorChain expect.
  React.useEffect(() => {
    if (!id || !entities || !relationships) return;
    const entityByGuid = new Map<string, any>();
    for (const e of entities) {
      const guid = e?.entity_guid ?? e?.entityGuid;
      if (!guid) continue;
      entityByGuid.set(String(guid).toUpperCase(), {
        entityGuid: guid,
        slug: e?.slug ?? null,
        entityType: e?.entity_type ?? e?.entityType ?? null,
      });
    }
    const parentMap = new Map<string, string>();
    for (const r of relationships) {
      const child = r?.child_entity_guid ?? r?.childEntityGuid;
      const parent = r?.parent_entity_guid ?? r?.parentEntityGuid;
      const rt = r?.relationship_type ?? r?.relationshipType;
      if (child && parent && (rt === 'parent_of' || rt === 'contains')) {
        parentMap.set(String(child).toUpperCase(), String(parent).toUpperCase());
      }
    }
    const entity = entityByGuid.get(String(id).toUpperCase());
    if (!entity) return;

    void (async () => {
      const { communityUrlFor, walkAncestorChain } = await import(
        './api/communityMetadata'
      );
      const ancestors = walkAncestorChain(entity, parentMap, entityByGuid);
      const url = communityUrlFor(entity, ancestors);
      // Only redirect if the helper produced a non-fallback (slug) URL.
      // The fallback is `/communities/{guid}` which is exactly where we already are.
      if (!url.startsWith('/communities/')) {
        // DEV-1026 — preserve hash + search when redirecting from the legacy
        // GUID URL to the slug URL. CommunityDetailPage reads location.hash
        // for its initial tab selection (e.g. /communities/{guid}#alignment
        // lands on the Alignment tab). Stripping the hash during redirect
        // meant deep links lost their tab selection on the slug-routed
        // remount; the original tab worked only by coincidence for first-
        // visit users whose default already matched.
        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        const search = typeof window !== 'undefined' ? window.location.search : '';
        navigate(url + search + hash, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, entities, relationships]);

  return (
    <CommunityDetailPage
      communityGuid={id || ''}
      currentUserGuid={currentUserGuid}
    />
  );
}

/**
 * DEV-888 / DEV-939. Resolves a slug URL to a community GUID, then renders
 * CommunityDetailPage with that GUID.
 *
 * Originally (DEV-888) resolved 1- and 2-segment org URLs (`/rotary` and
 * `/rotary/jamestown`). Extended in DEV-939 to handle variable-depth paths
 * (`/usa/california/oakland/uptown`) for geographic entities and any future
 * deepening of org hierarchies — the same machinery walks N segments down
 * the parent chain.
 *
 * While resolving, shows a tiny loading state. On 404, redirects to the
 * communities index. The existing `/communities/:id` route remains for
 * backwards compat and direct-GUID linking.
 */
function CommunitySlugRoute({ currentUserGuid }: { currentUserGuid: string }) {
  const params = useParams();
  const navigate = useNavigate();
  const [resolved, setResolved] = React.useState<string | null | undefined>(undefined);

  // Collect 1..N path segments from the route params. The route table
  // declares explicit patterns up to 5 segments; pull each one in order
  // and stop at the first undefined.
  const segments = React.useMemo(() => {
    const ordered = [
      params.rootSlug,
      params.subSlug,
      params.s3,
      params.s4,
      params.s5,
    ];
    const out: string[] = [];
    for (const s of ordered) {
      if (typeof s === 'string' && s.trim()) out.push(s);
      else break;
    }
    return out;
  }, [params.rootSlug, params.subSlug, params.s3, params.s4, params.s5]);

  // Use a stable cache key for the effect dependency.
  const segmentsKey = segments.join('/');

  React.useEffect(() => {
    let cancelled = false;
    if (segments.length === 0) return;
    void (async () => {
      try {
        // Lazy import keeps the slug API out of the initial bundle for non-slug routes.
        const { fetchCommunityBySlug, fetchCommunityBySlugPath } =
          await import('./api/communityMetadata');
        // Use the legacy 1/2-segment endpoint for those depths so backward-
        // compat URLs from DEV-888 hit exactly the same resolver they did
        // before. Fall through to the variable-depth resolver for 3+.
        const hit =
          segments.length <= 2
            ? await fetchCommunityBySlug(segments[0], segments[1])
            : await fetchCommunityBySlugPath(segments);
        if (!cancelled) setResolved(hit?.entityGuid ?? null);
      } catch {
        if (!cancelled) setResolved(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentsKey]);

  if (resolved === undefined) {
    return <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Loading…</div>;
  }
  if (resolved === null) {
    // DEV-947: render a proper 404 page that names the failed path and offers
    // escape routes. Previously this branch did `navigate('/communities')`
    // *during render* — a React anti-pattern that produced "Cannot update a
    // component during render" warnings, AND silently dumped the user on the
    // index page with no signal about what went wrong.
    return <CommunityNotFound attemptedPath={segmentsKey} />;
  }
  return (
    <CommunityDetailPage
      communityGuid={resolved}
      currentUserGuid={currentUserGuid}
    />
  );
}

function WorkspaceListRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <WorkspaceListPage
      communityGuid={id || ''}
      onOpen={(wsGuid) => navigate(`/communities/${id}/workspaces/${wsGuid}`)}
    />
  );
}

function WorkspaceDetailRoute({ currentUserGuid }: { currentUserGuid: string }) {
  const { id, workspaceId } = useParams<{ id: string; workspaceId: string }>();
  const navigate = useNavigate();
  // DEV-1452: no longer wired to a back button — this is the post-soft-delete
  // exit only (the route's workspace no longer exists).
  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/communities/${id}`);
    }
  }, [id, navigate]);
  return (
    <WorkspaceDetailPage
      workspaceGuid={workspaceId || ''}
      currentUserGuid={currentUserGuid}
      onBack={goBack}
    />
  );
}

/** Top-level workspace detail route for personal/household workspaces (no community context). */
function StandaloneWorkspaceDetailRoute({ currentUserGuid }: { currentUserGuid: string }) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/profile');
    }
  }, [navigate]);
  return (
    <WorkspaceDetailPage
      workspaceGuid={workspaceId || ''}
      currentUserGuid={currentUserGuid}
      onBack={goBack}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Passcode change modal — admin-only, used in gear menu               */
/* ------------------------------------------------------------------ */

function PasscodeChangeModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<'success' | 'mismatch' | 'error' | null>(null);

  const handleSave = async () => {
    if (newPasscode !== confirmPasscode) {
      setResult('mismatch');
      return;
    }
    if (!newPasscode.trim()) return;
    setSaving(true);
    setResult(null);
    try {
      await updateGlobePasscode(newPasscode);
      setResult('success');
      setNewPasscode('');
      setConfirmPasscode('');
    } catch {
      setResult('error');
    } finally {
      setSaving(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 8000,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-default)',
    borderRadius: 14, width: '90vw', maxWidth: 420,
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
    overflow: 'hidden',
  };
  const headerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)',
  };
  const inputStyle: React.CSSProperties = {
    background: 'var(--bg-secondary)', border: '1px solid var(--border-default)',
    borderRadius: 6, padding: '8px 10px', color: 'var(--text-primary)',
    fontSize: 14, width: '100%', boxSizing: 'border-box',
  };
  const footerStyle: React.CSSProperties = {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    padding: '12px 20px', borderTop: '1px solid var(--border-subtle)',
  };

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
            {t('appGear.passcodeModal.title')}
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}
          >&times;</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px' }}>
          <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('appGear.passcodeModal.new')}
          </label>
          <input
            type="text"
            style={inputStyle}
            value={newPasscode}
            onChange={(e) => { setNewPasscode(e.target.value); setResult(null); }}
            autoFocus
            disabled={saving}
          />
          <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('appGear.passcodeModal.confirm')}
          </label>
          <input
            type="text"
            style={inputStyle}
            value={confirmPasscode}
            onChange={(e) => { setConfirmPasscode(e.target.value); setResult(null); }}
            disabled={saving}
          />
          {result === 'mismatch' && (
            <div style={{ color: 'var(--status-red)', fontSize: 13 }}>
              {t('appGear.passcodeModal.mismatch')}
            </div>
          )}
          {result === 'error' && (
            <div style={{ color: 'var(--status-red)', fontSize: 13 }}>
              {t('appGear.passcodeModal.error')}
            </div>
          )}
          {result === 'success' && (
            <div style={{ color: 'var(--status-green)', fontSize: 13 }}>
              {t('appGear.passcodeModal.success')}
            </div>
          )}
        </div>
        <div style={footerStyle}>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', borderRadius: 6, padding: '6px 16px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !newPasscode.trim()}
            style={{
              background: saving || !newPasscode.trim() ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
              border: 'none', borderRadius: 6, padding: '6px 16px',
              color: saving || !newPasscode.trim() ? 'var(--text-muted)' : '#fff',
              cursor: saving || !newPasscode.trim() ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            {saving ? t('common:status.saving') : t('common:button.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Language indicator — compact flag button with dropdown              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  App                                                                */
/* ------------------------------------------------------------------ */

function App() {
  // DEV-932: dev-user-switcher source. Reads security_users from the API
  // (DB-seeded by migration 010). Replaces the previous flatfile import.
  // The hook is also called further down for unrelated security data;
  // each call shares the same query-cache key, so this isn't a duplicate
  // network round-trip — just a duplicate hook subscription.
  const { securityUsers: switcherUsers } = useApiSecurityData();
  const securityUsers = useMemo<any[]>(
    () => switcherUsers ?? [],
    [switcherUsers]
  );

  // DEV-1457 — the gear's simulated-user <select> (and its userOptions memo)
  // is gone; simulation moved to the admin console. `securityUsers` above is
  // still read below for the current user's row / default-community pref.

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  /* DEV-940: BrowserRouter eliminated the dual-URL-space problem this
   * effect existed to paper over. Under BrowserRouter, the pathname IS
   * the route — no separate hash universe to keep in sync, no clobbering
   * to clean up. The effect (and its DEV-947 followup that re-ran on
   * every location change) was tactical scaffolding that's now obsolete.
   */  

  // -----------------------------
  // State
  // -----------------------------
  const [importProjectsOpen, setImportProjectsOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [kpiMode, setKpiMode] = useState<KpiMode>('live');
  const [projectConfigVersion, setProjectConfigVersion] = useState(0);

  // Bump when project config changes (KPI instance configs, project overrides)
  useEffect(() => {
    const EVT = 'l4e_project_updated';
    const EVT_CFG = 'l4e_project_config_changed';
    const LS_KEY = 'l4e_project_config_overrides';
    const bump = () => setProjectConfigVersion((v) => v + 1);
    const onUpdated = () => bump();
    const onStorage = (e: StorageEvent) => { if (e?.key === LS_KEY) bump(); };
    window.addEventListener(EVT, onUpdated as any);
    window.addEventListener(EVT_CFG, onUpdated as any);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVT, onUpdated as any);
      window.removeEventListener(EVT_CFG, onUpdated as any);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Bump when schedule progress data changes (user edits via ScheduleThermometer).
  // This ensures the KpiGridTable re-scores when progress curves are updated.
  //
  // NOTE: Do NOT call invalidateAndRefetch here — the Apply flow writes to
  // the in-memory store synchronously, and the KPI useMemo reads from the
  // store (not the API cache). Cache invalidation is handled separately by
  // signalrConnection.ts via debouncedInvalidate when the server echoes back.
  //
  // Cross-tab sync is handled by BroadcastChannel (crossTabSync.ts) which
  // directly updates the in-memory store on receiving tabs. All version bump
  // events are accepted — no source filtering needed.
  const [progressDataVersion, setProgressDataVersion] = useState(0);
  useEffect(() => {
    const PROGRESS_EVT = 'l4e_schedule_progress_changed';
    const bump = () => setProgressDataVersion((v) => v + 1);
    window.addEventListener(PROGRESS_EVT, bump as any);
    return () => window.removeEventListener(PROGRESS_EVT, bump as any);
  }, []);

  // Bump when finance/budget cost data changes (user edits via BudgetThermometer).
  const [budgetDataVersion, setFinanceDataVersion] = useState(0);

  useEffect(() => {
    const bump = () => setFinanceDataVersion((v) => v + 1);
    window.addEventListener('l4e_finance_cost_changed', bump as any);
    return () => window.removeEventListener('l4e_finance_cost_changed', bump as any);
  }, []);

  // ── SignalR real-time connections ──
  // Chat hub + bridges connect on mount (always-on for all users).
  // KPI hub connects only when the user is on a route that consumes real-time
  // KPI data (portfolio, projects, engagements). This avoids firing
  // ScheduleUpdated/FinanceUpdated events that bump version counters and
  // trigger expensive Data useMemo recomputation for users who don't need it.

  // DEV-1023 — historically this list only included a few KPI-heavy routes
  // (portfolio / projects / my-projects / engagements) because real-time
  // KPI updates were the only thing the hub broadcast. As of DEV-1023 the
  // same hub now carries workflow_request and security events, which are
  // cross-cutting — a user on the Action Center page or Community Settings
  // needs WorkflowUpdated / SecurityUpdated regardless of route. Keep the
  // prefix array for reference (and in case future code wants to gate any
  // PER-ROUTE behavior), but disconnect-by-route is no longer correct.
  // The hub now stays connected whenever the user is authenticated.
  const KPI_ROUTE_PREFIXES = useMemo(() => [
    '/portfolio',
    '/projects',
    '/my-projects',
    '/engagements',
  ], []);
  void KPI_ROUTE_PREFIXES;

  /** Whether the KPI hub is currently connected (or should be). */
  const kpiHubConnectedRef = useRef(false);

  // DEV-1023 — Hub stays connected for the entire authenticated session.
  // The KpiHub now carries cross-cutting WorkflowUpdated + SecurityUpdated
  // events on top of KPI data, and those need to reach every session
  // regardless of which page the user is currently viewing. The Approvals
  // badge in the left nav and the Action Center inbox both depend on it.
  //
  // Note: this useMemo runs in the global App component which only mounts
  // for authenticated sessions (the public landing flow renders elsewhere),
  // so unconditional `true` here is equivalent to "authenticated user is
  // present" without needing to reference state that hasn't been declared
  // yet at this point in the function body.
  const needsKpiHub = useMemo(() => true, []);

  // Chat hub, health monitor, notification + presence bridges — always on.
  useEffect(() => {
    connectChatHub();
    startHealthMonitor();
    const stopNotifBridge = startNotificationBridge(currentUserGuid);
    const stopPresenceBridge = startPresenceBridge();
    return () => {
      disconnectChatHub();
      stopHealthMonitor();
      stopNotifBridge();
      stopPresenceBridge();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KPI hub — connect/disconnect based on current route.
  useEffect(() => {
    if (needsKpiHub && !kpiHubConnectedRef.current) {
      console.log('[App] Route needs KPI data — connecting KPI hub');
      kpiHubConnectedRef.current = true;
      connectKpiHub();
    } else if (!needsKpiHub && kpiHubConnectedRef.current) {
      console.log('[App] Route does not need KPI data — disconnecting KPI hub');
      kpiHubConnectedRef.current = false;
      disconnectKpiHub();
    }
    return () => {
      // Cleanup on unmount: disconnect if connected
      if (kpiHubConnectedRef.current) {
        kpiHubConnectedRef.current = false;
        disconnectKpiHub();
      }
    };
  }, [needsKpiHub]);

  // ── BroadcastChannel cross-tab sync ──
  // Direct tab-to-tab data sharing — supplements SignalR for same-browser sync.
  // When another tab edits schedule/finance data, this listener receives the
  // updated points, writes them to the in-memory store, and dispatches events
  // to trigger version bumps + useMemo re-scoring.
  useEffect(() => {
    const cleanup = startCrossTabListener();
    return () => { cleanup(); closeCrossTabChannel(); };
  }, []);

  // ── Idle timeout & kiosk mode (§4 Refresh Policy) ──
  // Parse URL parameters: ?mode=kiosk or ?idleTimeout=N (minutes, 0 = no timeout).
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const kioskMode = urlParams.get('mode') === 'kiosk' || urlParams.get('idleTimeout') === '0';
  const idleTimeoutMinutes = useMemo(() => {
    const raw = urlParams.get('idleTimeout');
    if (raw !== null) {
      const n = Number(raw);
      return (Number.isFinite(n) && n >= 0) ? n : 15;
    }
    return kioskMode ? 0 : 15; // Default: 15 minutes; kiosk = no timeout
  }, [urlParams, kioskMode]);

  const [connectionPaused, setConnectionPaused] = useState(false);

  const handleIdle = useCallback(() => {
    console.log('[App] Idle timeout — disconnecting SignalR');
    setConnectionPaused(true);
    // Only idle-disconnect the KPI hub if it was connected (route-gated)
    if (kpiHubConnectedRef.current) {
      idleDisconnect();
    }
    idleDisconnectChat();
  }, []);

  const handleResume = useCallback(() => {
    console.log('[App] User activity detected — reconnecting SignalR');
    setConnectionPaused(false);
    // Only reconnect the KPI hub if the current route still needs it
    if (kpiHubConnectedRef.current) {
      reconnectAfterIdle();
    }
    reconnectChatAfterIdle();
  }, []);

  useIdleDetector({
    timeoutMs: idleTimeoutMinutes * 60 * 1000,
    kioskMode,
    onIdle: handleIdle,
    onResume: handleResume,
  });

  // Report this user's visibility/idle status to other clients via SignalR.
  // Event-driven: sends one message per online↔away transition, no polling.
  usePresenceReporter({ disabled: kioskMode });

  const [debugMode, setDebugMode] = useState(() => {
    try { return window?.localStorage?.getItem('l4e_debug_mode') === 'true'; }
    catch { return false; }
  });

  // User identity: read from URL `?user=GUID` via client module (no localStorage).
  const [currentUserGuid, setCurrentUserGuidState] = useState(() => getCurrentUserGuid());
  // DEV-1389 — per-user topbar search display preference (device-local).
  const searchBarMode = useSearchBarMode(currentUserGuid);
  // Explorer feature-gating: the My Impact + Action Center surfaces appear once
  // the user has the impact_radar feature (their first real contribution).
  // Permissive for legacy/admin users with no archetype — matches <FeatureGate>.
  const {
    hasFeature: hasOnboardingFeature,
    archetype: onboardingArchetype,
    archetypeConfig: onboardingConfig,
    unlockedFeatures: onboardingFeatures,
    achievedMilestones: onboardingFlags,
    recordMilestone: recordOnboardingMilestone,
    loading: onboardingLoading,
    invitedCommunityGuid: onboardingInvitedCommunityGuid,
  } = useApiOnboarding(currentUserGuid);
  // System admins always see the impact surfaces (My Impact / Impact
  // Signature), regardless of onboarding archetype — same §6 caveat as the
  // nav gates. Guards against a backfilled 'explorer' profile hiding them.
  const showImpactSurfaces =
    getIsSystemAdmin() ||
    (!onboardingLoading && (!onboardingArchetype || hasOnboardingFeature('impact_radar')));
  // §7.16 beat 2 (chunk B slice D) — the ARRIVAL window: promoted, but the
  // reveal not yet seen (server radar_explained flag absent). During this
  // window the rail's signature card materializes with its one-shot
  // animation; once the explainer is dismissed the flag lands and every
  // later render is static. One source of truth with the signature page.
  const signatureArriving =
    showImpactSurfaces &&
    !onboardingLoading &&
    onboardingFlags.has('contributor_promoted') &&
    !onboardingFlags.has('radar_explained');
  // The Action Center is every user's home for their base-role (role #0)
  // onboarding/Setup obligations + all role actions, so it shows for ALL users
  // — including brand-new members who don't yet have the impact_radar feature.
  // Decoupled from showImpactSurfaces (which still gates the mini Impact radar).
  // DEV-1368 / Slice C precondition (Skip 2026-06-23).
  const showActionCenterForAll = true;
  // Nav gating: tier groups visible per rung, items per feature flag.
  // Permissive (undefined) while loading or for no-archetype (legacy) users.
  const navVisibleTiers = useMemo<Set<TierKey> | undefined>(() => {
    // System admins ALWAYS see all tiers, regardless of onboarding archetype
    // (POLICY_NAVIGATION_RAILS §6 caveat). Prior to the DEV-1302 profile
    // backfill, admins had no profile and fell through to the permissive
    // no-archetype branch below; once a backfilled 'explorer' profile exists,
    // that branch would wrongly gate them to Explore only. Guard explicitly
    // with the JWT-derived admin flag (set at login; currentUserGuid in deps
    // recomputes this across login/logout transitions).
    if (getIsSystemAdmin()) return undefined;

    const key = onboardingConfig?.key;
    if (onboardingLoading || !key) return undefined;
    const map: Record<string, TierKey[]> = {
      explorer: ['explore'],
      contributor: ['explore', 'contribute'],
      leader: ['explore', 'contribute', 'lead'],
      governor: ['explore', 'contribute', 'lead', 'govern'],
    };
    return new Set<TierKey>(map[key] ?? ['explore', 'contribute', 'lead', 'govern']);
    // Intentional: currentUserGuid is not read in the closure, but it must
    // stay in deps so getIsSystemAdmin() (a module-scope read, invisible to
    // the linter) re-evaluates across login/logout — see guard comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingLoading, onboardingConfig, currentUserGuid]);
  /* ── DEV-1482 — per-item feature gating, WITHOUT the fail-open flash ──
   *
   * THE OLD BUG: `undefined` meant two different things — "system admin, show
   * everything" AND "still loading". LeftNav could only honour one reading, and
   * it chose fail-OPEN, so every login flashed the higher tiers before the fetch
   * landed and hid them. An Explorer glimpsing Champion/Govern is a
   * progressive-disclosure spoiler, not just jank.
   *
   * THE FIX: this now always resolves to a REAL SET, so LeftNav has no
   * ambiguous branch left to get wrong.
   *   sysadmin        → every flag (explicitly, not by absence)
   *   loaded          → the server's set (and we cache it for next time)
   *   loading, cached → the last-known set for THIS user (GUID-namespaced)
   *   loading, cold   → EMPTY. Fail closed. A first-time user IS an Explorer,
   *                     so ungated-items-only is correct by construction.
   *
   * The cache seeds PIXELS, never permissions — gated actions still check the
   * live set, and a stale seed corrects silently on fetch.
   */
  const navFeaturesLoaded = !onboardingLoading && !!onboardingConfig;

  const navFeatures = useMemo<Set<FeatureFlag>>(() => {
    if (getIsSystemAdmin()) return new Set(ALL_FEATURE_FLAGS);
    if (navFeaturesLoaded) return onboardingFeatures;
    return readCachedNavFeatures(currentUserGuid) ?? NAV_FEATURES_FAIL_CLOSED;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navFeaturesLoaded, onboardingFeatures, currentUserGuid]);

  // Refresh the seed once the server's answer arrives. Keyed by user, so the next
  // login for THIS user paints correctly — and so a different user on the same
  // browser can never inherit it (DEV-1443).
  useEffect(() => {
    if (!navFeaturesLoaded || !currentUserGuid) return;
    if (getIsSystemAdmin()) return;   // don't cache the admin's all-flags view
    writeCachedNavFeatures(currentUserGuid, onboardingFeatures);
  }, [navFeaturesLoaded, onboardingFeatures, currentUserGuid]);

  // ── My Impact: come back to where you left off (Skip 2026-07-13) ──────────
  // The nav item always landed on /memberships, whose main pane IS the Impact
  // Signature — so a member who had been reading their community got dumped back
  // on the signature every time and had to hunt for the community again.
  // Remember the last path visited INSIDE the section and navigate there.
  //
  // Refines (does not undo) DEV-1458 §7.7 ruling 5: the click still never
  // RESOLVES a community for you — the landing resolver stays out of it. It just
  // stops discarding where you already were. Defaults to the section home.
  //
  // Scoped per user so the preference never leaks across accounts (the same rule
  // the other LeftNav prefs follow).
  const myImpactPathKey = currentUserGuid
    ? `l4e_myimpact_last_path:${currentUserGuid.toUpperCase()}`
    : null;
  const [myImpactRoute, setMyImpactRoute] = useState<string>('/memberships');

  // Restore on mount / account switch.
  useEffect(() => {
    if (!myImpactPathKey) { setMyImpactRoute('/memberships'); return; }
    try {
      const saved = window.localStorage.getItem(myImpactPathKey);
      // Guard: only ever trust a path inside the section.
      setMyImpactRoute(saved && saved.startsWith('/memberships') ? saved : '/memberships');
    } catch {
      setMyImpactRoute('/memberships');
    }
  }, [myImpactPathKey]);

  // Remember the last in-section path as the user moves around.
  useEffect(() => {
    if (!myImpactPathKey) return;
    const p = location.pathname;
    if (!p.startsWith('/memberships')) return;
    setMyImpactRoute(p);
    try {
      window.localStorage.setItem(myImpactPathKey, p);
    } catch {
      /* private mode / quota — non-fatal; we just don't remember. */
    }
  }, [location.pathname, myImpactPathKey]);

  // ── Contributor promotion reveal (DEV-428) ────────────────────────────
  // Fire the "Welcome to Contributor" moment once, the first time the client
  // sees the user as a contributor — whether the auto-promotion happened live
  // (focus-refetch flips the archetype) or on a later reload. Persisted
  // per-user in localStorage: shows once per device, survives reloads, and
  // naturally welcomes invitation-granted contributors too.
  const [showContributorWelcome, setShowContributorWelcome] = useState(false);
  const contributorWelcomeKey = currentUserGuid
    ? `l4e_contributor_welcomed:${currentUserGuid.toUpperCase()}`
    : null;
  useEffect(() => {
    if (onboardingLoading) return;
    // DEV-1443: fire off the SERVER promotion milestone for the CURRENT user
    // (contributor_promoted present AND contributor_promotion_ack absent) — the
    // same server-truth gate LeftNav and the Action Center already use — NOT the
    // archetype key, which could be a stale/racy value during an identity switch
    // on a shared browser (that mis-fired the modal for a brand-new Explorer).
    // localStorage is a per-device BELT (suppresses duplicate firing), never the
    // trigger.
    if (!onboardingFlags.has('contributor_promoted')) return;
    if (onboardingFlags.has('contributor_promotion_ack')) return;
    if (!contributorWelcomeKey) return;
    try {
      if (window.localStorage.getItem(contributorWelcomeKey) === '1') return;
    } catch { return; }
    setShowContributorWelcome(true);
  }, [onboardingLoading, onboardingFlags, contributorWelcomeKey]);
  const dismissContributorWelcome = useCallback(() => {
    try {
      if (contributorWelcomeKey) window.localStorage.setItem(contributorWelcomeKey, '1');
    } catch { /* localStorage unavailable — just close */ }
    setShowContributorWelcome(false);
  }, [contributorWelcomeKey]);
  // Re-open on request (Skip 2026-07-07): the Action Center's Done tray for
  // the acknowledged promotion Notice offers "show the welcome again" — the
  // once-per-device gate above governs only the AUTOMATIC firing; explicit
  // requests always work (resolved content stays reachable, §7.9).
  useEffect(() => {
    const reopen = () => setShowContributorWelcome(true);
    window.addEventListener('l4e:show-contributor-welcome', reopen);
    return () => window.removeEventListener('l4e:show-contributor-welcome', reopen);
  }, []);

  // ── "Welcome to Life4Earth" first-login screen (Skip 2026-07-07) ──
  // Fires ONCE per user (server-side welcome_screen_seen flag, §7.9 r4 —
  // never localStorage) for any account WITH an onboarding profile that
  // hasn't seen it. NOT a wizard (§7.9): one screen, dismiss and you're in;
  // it stays reachable via the "Read me first" checklist row and the admin
  // Copy-tab preview. Legacy null-archetype accounts never see it. Yields
  // to the Contributor welcome if both are somehow pending.
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const welcomeScreenFiredRef = useRef(false);
  useEffect(() => {
    if (welcomeScreenFiredRef.current) return;
    if (onboardingLoading || !onboardingArchetype) return;
    if (onboardingFlags.has('welcome_screen_seen')) return;
    if (showContributorWelcome) return;
    welcomeScreenFiredRef.current = true;
    setShowWelcomeScreen(true);
  }, [onboardingLoading, onboardingArchetype, onboardingFlags, showContributorWelcome]);
  const dismissWelcomeScreen = useCallback(() => {
    setShowWelcomeScreen(false);
    recordOnboardingMilestone({ milestoneKey: 'welcome_screen_seen' }).catch(() => {
      welcomeScreenFiredRef.current = false; // failed write → allow re-offer
    });
  }, [recordOnboardingMilestone]);

  // Sync currentUserGuid from real auth (JWT) when user logs in via OAuth or email.
  // In FakeAuth mode, auth.user comes from the hardcoded dev identity so this is a no-op.
  const auth = useAuth();
  // DEV-952: track previous auth user so we can force a SignalR reconnect on
  // the FIRST resolution of auth.user (the transition from null → identity),
  // not just on subsequent identity changes. Earlier code gated the reconnect
  // on `jwtGuid !== currentUserGuid`, but if the URL already had the correct
  // ?user=<GUID> param (a common case after impersonation testing), the two
  // matched on first auth resolution and the reconnect never fired — leaving
  // SignalR running with the unauthenticated handshake from before silentRefresh
  // had completed.
  const prevAuthUserGuidRef = useRef<string | null>(null);
  useEffect(() => {
    if (auth.user?.userGuid && !auth.isFakeAuth) {
      const jwtGuid = auth.user.userGuid.toUpperCase();
      if (jwtGuid !== currentUserGuid.toUpperCase()) {
        setCurrentUserGuidState(jwtGuid);
      }
      // Force SignalR reconnect on transition from no-auth → authenticated, OR
      // on identity change. The first SignalR connection at App mount races
      // AuthContext.silentRefresh — if SignalR connected before silentRefresh
      // completed, accessTokenFactory returned null, no JWT was sent, the
      // server treated the connection as anonymous (empty userGuid),
      // OnConnectedAsync joined no groups, and all NewMessage broadcasts
      // bypassed the connection forever. Tearing down + reconnecting after
      // auth resolves guarantees the new handshake carries the JWT.
      if (prevAuthUserGuidRef.current !== jwtGuid) {
        // DEV-1443: a REAL identity change (user A → user B in the same JS
        // session, e.g. registering/logging in as a different user on a shared
        // browser without a reload) must flush all per-user client caches
        // BEFORE the new identity's data loads — otherwise the prior user's
        // cached state (e.g. a stale 'contributor' archetype) drives UI like
        // the promotion modal. NOT on the null→identity bootstrap (nothing to
        // leak; would needlessly wipe freshly-hydrated same-user data on
        // reload). Logout does its own flush (AuthContext), covering the
        // A-logout → B-login path where prevAuthUserGuidRef resets to null.
        const hadPriorIdentity = prevAuthUserGuidRef.current !== null;
        prevAuthUserGuidRef.current = jwtGuid;
        if (hadPriorIdentity) {
          flushClientStateForIdentitySwitch();
          // DEV-1443 gap fix (2026-07-08 audit): the flush clears CACHES, but
          // React modal state is its own surface — a welcome/promotion modal
          // fired (or open) for user A must never carry into user B's render.
          setShowContributorWelcome(false);
          setShowWelcomeScreen(false);
          welcomeScreenFiredRef.current = false;
        }
        disconnectChatHub().then(() => connectChatHub()).catch((err) => {
          console.warn('[App] Failed to reconnect ChatHub after auth:', err);
        });
      }
    } else if (!auth.user?.userGuid) {
      prevAuthUserGuidRef.current = null;
    }
  }, [auth.user?.userGuid, auth.isFakeAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
    Push the active "viewed-as" user GUID into AccountSettingsContext so
    the modal (which is mounted as a sibling of <App/>) sees the correct
    user when admins use the gear-menu user switcher. Without this, the
    modal calls `getCurrentUserGuid()` from api/client which returns the
    JWT-authenticated user (the admin) instead of the gear-menu-selected
    user (the impersonated viewer).
  */
  const accountSettings = useAccountSettings();
  useEffect(() => {
    accountSettings.setActiveUserGuid(currentUserGuid);
  }, [currentUserGuid, accountSettings]);

  // Impact Signature: tier data for the left nav mini radar
  // App-level useApiImpactSignature removed with the mini-radar (§7.6 /
  // DEV-1446 — no orphaned fetches): the My Impact rail card does its own
  // gated read; the full signature page keeps its own.

  // DEV-1050 cutover complete — projectAclVersion bump state removed
  // along with the V1 securityEval useMemo that consumed it. V2 caps
  // invalidate via SignalR + useV2ProjectRoles re-runs; no manual bump
  // needed on the App-level visible-projects derivation.

  const onUserChange = useCallback((userGuid: string) => {
    const next = String(userGuid || '');
    setCurrentUserGuidState(next);
    setCurrentUserGuid(next); // Updates module-level state + browser URL

    // Reconnect ChatHub so the SignalR connection authenticates as the new user.
    // Without this, presence and messaging stay bound to the previous identity.
    disconnectChatHub().then(() => connectChatHub()).catch((err) => {
      console.warn('[App] Failed to reconnect ChatHub after user change:', err);
    });
  }, []);

  // ── Messages badge: count of conversations with unread messages + pending requests ──
  // Refresh on mount + whenever a new chat message arrives via SignalR.
  useEffect(() => {
    const refreshBadge = () => {
      Promise.all([
        fetchChatConversations().catch((): any[] => []),
        fetchChatPendingRequests().catch((): any[] => []),
      ]).then(([convs, reqs]: [any[], any[]]) => {
        // Count conversations that have unread messages (not total message count)
        const unreadConversations = (convs as any[]).filter((c) => (c.unreadCount || 0) > 0).length;
        const pendingInbound = (reqs as any[]).filter(
          (r) => r.recipientGuid?.toUpperCase() === currentUserGuid.toUpperCase()
        ).length;
        setMessagesBadge(unreadConversations + pendingInbound);
      });
    };

    refreshBadge();
    // Play notification sound on new message arrival (gated by tab visibility + user pref)
    const unsub = onChatEvent(ChatHubEvents.NewMessage, () => {
      playNotificationSound();
      refreshBadge();
    });
    // When invited to a new conversation, join the SignalR group immediately
    // so we can receive messages even before opening the chat UI
    const unsubInvite = onChatEvent(ChatHubEvents.ConversationInvite, (data: any) => {
      if (data?.conversationId) {
        joinConversationGroup(data.conversationId).catch(() => {});
      }
      refreshBadge();
    });
    // Refresh badge when read receipts arrive (messages marked as read)
    const unsubRead = onChatEvent(ChatHubEvents.ReadReceipt, refreshBadge);
    // Refresh badge when a contact request is accepted
    const unsubAccepted = onChatEvent(ChatHubEvents.ContactAccepted, refreshBadge);
    // Listen for local "badges changed" events (e.g., from markAllRead or ChatWindow read)
    const handleBadgeRefresh = () => refreshBadge();
    window.addEventListener('l4e_badges_changed', handleBadgeRefresh);
    // Also poll every 60s for pending requests (no real-time event for those yet)
    const interval = setInterval(refreshBadge, 60000);

    return () => {
      unsub(); unsubInvite(); unsubRead(); unsubAccepted();
      window.removeEventListener('l4e_badges_changed', handleBadgeRefresh);
      clearInterval(interval);
    };
  }, [currentUserGuid]);

  // (The old approvals-badge effect is gone — §7.14: the Action Center badge
  // is composed by useActionItemComposer below, one set with the inbox.)

  const onDebugModeChange = useCallback((v: boolean) => {
    setDebugMode(v);
    try { window?.localStorage?.setItem('l4e_debug_mode', String(v)); }
    catch { /* ignore */ }
  }, []);

  // App-level gear menu
  const [appGearOpen, setAppGearOpen] = useState(false);
  const appGearRef = useRef<HTMLDivElement | null>(null);
  const [apiStatusOpen, setApiStatusOpen] = useState(false);
  // Geography admin moved to /admin/geo (was modal, now full page)
  const [passcodeModalOpen, setPasscodeModalOpen] = useState(false);

  // Chat tray (bottom-right floating panel)
  const [chatTrayOpen, setChatTrayOpen] = useState(false);

  // DEV-1256. Imperative "open the chat tray and start a DM with this user"
  // entry point. Any surface in the app can dispatch a
  // `window.dispatchEvent(new CustomEvent('l4e:open-chat-with', { detail: { userGuid } }))`
  // and the tray opens + jumps to (or creates) a direct conversation with
  // that user. Used by the Action Center / Engagements task trays' Message
  // button so users don't have to leave their current page to send a quick
  // contact message.
  const [chatStartWithUserGuid, setChatStartWithUserGuid] = useState<string | null>(null);
  useEffect(() => {
    const handler = (ev: Event) => {
      const ce = ev as CustomEvent<{ userGuid?: string }>;
      const userGuid = ce.detail?.userGuid;
      if (!userGuid) return;
      setChatTrayOpen(true);
      setChatStartWithUserGuid(userGuid);
    };
    window.addEventListener('l4e:open-chat-with', handler as EventListener);
    return () => window.removeEventListener('l4e:open-chat-with', handler as EventListener);
  }, []);

  // API health monitoring — used for disabled icon states
  const apiHealth = useApiHealth();
  const chatApiUp = apiHealth.endpoints.find((e) => e.name === "Chat");
  const isChatOffline = chatApiUp != null && chatApiUp.status === "down";

  // Messages badge: count of conversations with unread messages + pending contact requests
  const [messagesBadge, setMessagesBadge] = useState(0);

  useEffect(() => {
    if (!appGearOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (appGearRef.current && e.target instanceof Node && !appGearRef.current.contains(e.target))
        setAppGearOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [appGearOpen]);

  // -----------------------------
  // Data computation — API-first with seed fallback
  // -----------------------------
  const seedFallback = useCallback(() => {
    const all = getAllProjectsSeedPlusAdded();
    return mergeProjectsWithOverrides(all as any);
  }, []);

  const { projects: apiProjects, source: dataSource, loading: apiLoading, refetch: refetchProjects } = useApiProjects(seedFallback);

  // Mini-Projects for GlobalSearch (backend filters by visibility/access).
  const { miniProjects: apiMiniProjects } = useApiMiniProjects();
  // DEV-1116 Phase 1b. Bulk per-MP latest-status + goal-assessment data.
  // Failure mode is silent (empty array) — the aggregator falls back to
  // Phase 1a coloring (DR + lifecycle only) if this fetch is unreachable.
  const { signals: apiMpPortfolioSignals } = useApiMiniProjectPortfolioSignals();

  const scheduleSeedFallback = useCallback(() => {
    return scheduleProgressData as any[];
  }, []);

  const {
    scheduleData: apiScheduleData,
    raw: apiScheduleRaw,
    source: scheduleSource,
    loading: scheduleLoading,
    refetch: refetchSchedule,
  } = useApiScheduleData(scheduleSeedFallback);

  const {
    configsByProject: apiKpiConfigs,
    source: kpiConfigSource,
    loading: kpiConfigLoading,
    refetch: refetchKpiConfigs,
  } = useApiKpiConfigs();

  // No seed-data fallbacks: mission shows empty until real data is configured via KPI Configuration.
  const {
    missionData: apiMissionData,
    missionTargetsData: apiMissionTargets,
    rawPoints: missionRawPoints,
    source: missionSource,
    loading: missionLoading,
    refetch: refetchMission,
  } = useApiMissionData();

  // Refetch mission data AND KPI configs when projectConfigVersion bumps (user saved
  // a metric point, updated a target, or changed KPI config). Skip initial mount (version 0).
  // Without refetchKpiConfigs(), newly added mission subKPIs won't appear on the Mission
  // tab because apiKpiConfigs React state stays stale.
  useEffect(() => {
    if (projectConfigVersion > 0) {
      refetchMission();
      refetchKpiConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectConfigVersion]);

  const {
    budgetData: apiFinanceData,
    raw: apiFinanceRaw,
    source: financeSource,
    loading: financeLoading,
    refetch: refetchFinance,
  } = useApiBudgetData();

  const {
    raw: apiCurveMetaRaw,
    scheduleMeta: apiScheduleMeta,
    budgetMeta: apiBudgetMeta,
    source: curveMetaSource,
    loading: curveMetaLoading,
    refetch: refetchCurveMeta,
  } = useApiCurveMeta();

  const {
    raw: apiKpiProgressRaw,
    entries: apiKpiProgressEntries,
    source: kpiProgressSource,
    loading: kpiProgressLoading,
    refetch: refetchKpiProgress,
  } = useApiKpiProgress();

  const {
    raw: apiWorkflowsRaw,
    workflows: apiWorkflowEntries,
    source: workflowsSource,
    loading: workflowsLoading,
    refetch: refetchWorkflows,
  } = useApiWorkflows();

  const {
    loading: mediaStatsLoading,
    source: mediaStatsSource,
    countLabel: mediaStatsLabel,
    refetch: refetchMediaStats,
  } = useMediaStats();

  const {
    communityEntities: apiCommunityEntities,
    communityRelationships: apiCommunityRelationships,
    collections: apiCollections,
    collectionProjects: apiCollectionProjects,
    schemaFields: apiSchemaFields,
    metadataValues: apiMetadataValues,
    customEntities: apiCustomEntities,
    rawCounts: communityRawCounts,
    source: communitySource,
    loading: communityLoading,
    refetch: refetchCommunity,
  } = useApiCommunityData();

  // NOTE: refreshCommunityMaps is now called synchronously inside the
  // effectiveProjects useMemo (below) so that the module-level entity maps
  // are up-to-date *before* rederiveCommunityFields reads them.
  // Previously this lived in a useEffect, which runs *after* render —
  // causing a race where rederiveCommunityFields would read stale static
  // maps on first load, making new subcommunities show as "Unspecified".

  const {
    securityUsers: apiSecurityUsers,
    projectAcl: apiProjectAcl,
    communityRoles: apiCommunityRoles,
    communityMemberships: apiCommunityMemberships,
    collectionMemberships: apiCollectionMemberships,
    securityLinks: apiSecurityLinks,
    rawCounts: securityRawCounts,
    source: securitySource,
    loading: securityLoading,
    refetch: refetchSecurity,
  } = useApiSecurityData();

  // When the simulated user changes, refetch ALL data so the new user
  // sees their own projects, communities, and security permissions.
  const prevUserRef = useRef(currentUserGuid);
  useEffect(() => {
    if (prevUserRef.current !== currentUserGuid && currentUserGuid) {
      prevUserRef.current = currentUserGuid;
      refetchProjects();
      refetchCommunity();
      refetchSecurity();
    }
  }, [currentUserGuid, refetchProjects, refetchCommunity, refetchSecurity]);

  // ── Cache invalidation is handled by signalrConnection.ts ──
  // SignalR hub events call invalidateAndRefetch() for the relevant cache keys.
  // Local saves (thermometer edits) also trigger SignalR events via the backend,
  // so the cache invalidation path covers both local and remote changes.
  // No additional event listeners needed here — the old debounced refetch
  // block has been replaced by the query cache's built-in deduplication.

  // ── Hydrate in-memory store from API data ──
  // Once the API hooks resolve, populate the central store so that
  // override files read from memory (not localStorage).
  useEffect(() => {
    if (!scheduleLoading && scheduleSource === 'api') {
      hydrateScheduleSeries(apiScheduleData);
    }
  }, [scheduleLoading, scheduleSource, apiScheduleData]);

  useEffect(() => {
    if (!financeLoading && financeSource === 'api') {
      hydrateBudgetSeries(apiFinanceData);
    }
  }, [financeLoading, financeSource, apiFinanceData]);

  useEffect(() => {
    if (!curveMetaLoading && curveMetaSource === 'api') {
      hydrateCurveMeta(apiScheduleMeta, apiBudgetMeta);
    }
  }, [curveMetaLoading, curveMetaSource, apiScheduleMeta, apiBudgetMeta]);

  useEffect(() => {
    if (!kpiConfigLoading && kpiConfigSource === 'api') {
      hydrateKpiConfigs(apiKpiConfigs);
    }
  }, [kpiConfigLoading, kpiConfigSource, apiKpiConfigs]);

  useEffect(() => {
    if (!kpiProgressLoading && kpiProgressSource === 'api') {
      hydrateProgressEntries(apiKpiProgressEntries);
    }
  }, [kpiProgressLoading, kpiProgressSource, apiKpiProgressEntries]);

  useEffect(() => {
    if (!workflowsLoading && workflowsSource === 'api') {
      hydrateWorkflows(apiWorkflowEntries);
    }
  }, [workflowsLoading, workflowsSource, apiWorkflowEntries]);

  useEffect(() => {
    if (!apiLoading && dataSource === 'api' && apiProjects.length > 0) {
      // Hydrate project config store from API project data
      hydrateProjectConfigs(apiProjects);
      hydrateProjectStumps(apiProjects);
    }
  }, [apiLoading, dataSource, apiProjects]);

  useEffect(() => {
    if (!securityLoading && securitySource === 'api') {
      hydrateProjectAcl(apiProjectAcl);
      hydrateCommunityRoles(apiCommunityRoles);
      hydrateCommunityMemberships(apiCommunityMemberships);
      // DEV-1050 — slim hydration of securityAdapter (security-users only)
      // so the synchronous isUserSystemAdmin fast-path works without callers
      // threading the list through. The legacy multi-field hydration (the
      // V1 evaluator's data-injection plumbing) is gone.
      hydrateSecurityAdapter({ securityUsers: apiSecurityUsers });
    }
  }, [securityLoading, securitySource, apiProjectAcl, apiCommunityRoles, apiCommunityMemberships, apiSecurityUsers]);

  useEffect(() => {
    if (!communityLoading && communitySource === 'api') {
      hydrateCommunityConfigs(apiCommunityEntities);
      hydrateCollectionProjectLinks(apiCollectionProjects);
    }
  }, [communityLoading, communitySource, apiCommunityEntities, apiCollectionProjects]);

  // ── Hydrate project taxonomy from API ──
  // Loads the admin-managed category/type/subtype tree into the
  // runtime getters in projectStandardOptions.ts. Falls back to
  // hardcoded defaults if the API is unavailable or returns empty.
  useEffect(() => {
    let cancelled = false;
    getTaxonomyNodes()
      .then((nodes) => { if (!cancelled && nodes?.length) hydrateTaxonomyFromApi(nodes); })
      .catch(() => { /* fallback to hardcoded taxonomy */ });
    // Prime the embed allowlist from API (falls back to static list on failure)
    hydrateEmbedAllowlist().catch(() => { /* static fallback already in place */ });
    return () => { cancelled = true; };
  }, []);

  // Use API projects when available, fall back to seed+overrides
  const effectiveProjects = useMemo(() => {
    // Synchronously refresh the module-level community-entity maps *before*
    // rederiveCommunityFields reads them.  This ensures newly-created
    // sub-communities are recognized on the very first render after API data
    // arrives (including page refresh / app restart).
    if (communitySource === 'api' && (apiCommunityEntities?.length || apiCommunityRelationships?.length)) {
      refreshCommunityMaps(apiCommunityEntities, apiCommunityRelationships);
    }

    let projects: any[];
    if (dataSource === 'api') {
      // API projects are already in seed shape via toSeedShape()
      // Still apply localStorage overrides for any local patches
      projects = mergeProjectsWithOverrides(apiProjects as any);
    } else {
      // Fallback: seed data (returned by the hook when API is unreachable)
      projects = apiProjects as any[];
    }
    // Re-derive community_l1/l2/l3 from primary_location_entity_guid using
    // the latest community entity maps (which include API-created sub-communities).
    return rederiveCommunityFields(projects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiProjects, dataSource, projectConfigVersion, apiCommunityEntities, apiCommunityRelationships, communitySource]);

  // DEV-1050 cutover complete — the second hydrateSecurityAdapter() useEffect
  // (which fed effectiveProjects + apiCollections into the V1 evaluator) was
  // removed along with V1. V2 hooks compute their own per-project capabilities.

  // DEV-1033 — V2 grant cache for the current user. Builds the project
  // list shape the V2 evaluator expects (guid + visibility + collection
  // membership), then mounts useV2ProjectRoles which writes resolved
  // capabilities into a module-scope cache that canViewProject /
  // canEditProjectContent / canAdminProject consult before falling
  // through to V1. Mounting at App level rather than per-page means
  // every gate check in the app gets V2 reads for free; the existing
  // 22 consumer call sites need no modification.
  //
  // collectionGuids per project comes from apiCollectionProjects (the
  // collection_project junction). We build a one-pass index then look
  // up per project in the map.
  const v2_collectionGuidsByProject = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const cp of (apiCollectionProjects as any[]) || []) {
      const pg = String(cp.project_guid || cp.projectGuid || "").toUpperCase();
      const cg = String(cp.collection_guid || cp.collectionGuid || "");
      if (!pg || !cg) continue;
      const list = m.get(pg) ?? [];
      list.push(cg);
      m.set(pg, list);
    }
    return m;
  }, [apiCollectionProjects]);

  const v2_projectsForEval = useMemo(() => {
    return (effectiveProjects as any[]).map((p: any) => {
      const guid = String(p.project_guid || p.projectGuid || p.project_GUID || "");

      /* ⚠ DEV-1463 SAFETY PIN — do NOT wire `p.visibility` in here yet. ⚠
       *
       * This line used to read `String(p.visibility || "private")` against a
       * column that DID NOT EXIST, so every project evaluated as "private" and
       * the V2 evaluator's Rule 5 ("public-visibility default": union the
       * cascadable view set → grant it to EVERY authenticated user,
       * evaluator.ts:150) was permanently dormant.
       *
       * Migration 275 makes the column real AND backfills every pre-existing
       * project to 'public' (deliberately — they are readable by everyone today
       * and tightening them would retroactively hide published work).
       *
       * So the moment this reads the real column, Rule 5 fires for EVERY
       * project on the platform: every user gains view capability on everything,
       * `visibleProjectGuidSet` stops restricting, and Portfolio + GlobalSearch
       * flood with every project in the system. That is a permission WIDENING —
       * the exact opposite of what DEV-1463 exists to enable.
       *
       * The three-value model (team_only|community|public) and the evaluator's
       * two-value model (public|private) also do not agree, and reconciling them
       * is a real design decision, not a mapping.
       *
       * Both are DEV-1071's job (the GATE slice), where client and server
       * converge on one rule. Until then this stays pinned to the pre-migration
       * behaviour: evaluation is unchanged, and DEV-1463 is a true no-op for
       * permissions.
       *
       * The Discover catalog does not go through here — it reads the raw column
       * for DISCOVERABILITY, which is a different question from READABILITY.
       */
      const visibility: "public" | "private" = "private";

      return {
        projectGuid: guid,
        visibility,
        collectionGuids: v2_collectionGuidsByProject.get(guid.toUpperCase()) ?? [],
      };
    });
  }, [effectiveProjects, v2_collectionGuidsByProject]);

  const v2_is2FAEnabled = useMemo(() => {
    if (!currentUserGuid) return false;
    const me = String(currentUserGuid).toUpperCase();
    const user = (apiSecurityUsers || []).find(
      (u: any) => String(u.user_guid || u.userGuid || "").toUpperCase() === me,
    );
    return !!user?.is_two_factor_enabled;
  }, [apiSecurityUsers, currentUserGuid]);

  // DEV-1050 cutover complete — capture the V2 hook return so the
  // visible-projects useMemo below can react to caps changes. The hook
  // continues to populate the module-scope cache that the synchronous
  // gate functions (canViewProject / canEditProjectContent / canAdminProject)
  // read; the return-value capture is purely so App-level reactive memos
  // see updates without polling.
  const { capsByProject: v2_capsByProject, loading: v2RolesLoading } = useV2ProjectRoles({
    userGuid: currentUserGuid || "",
    is2FAEnabled: v2_is2FAEnabled,
    projects: v2_projectsForEval,
  });

  // DEV-1050 — sysadmin check, computed early so the V2-derived memos below
  // can apply the sysadmin override. (Previously this memo lived later in
  // the component; moved up so visible-projects derivation can reference it.)
  const currentUserIsAdmin = useMemo(() => {
    if (!currentUserGuid) return false;
    const guid = String(currentUserGuid).toUpperCase();
    return (apiSecurityUsers || []).some(
      (u: any) => String(u.user_guid || u.userGuid || '').toUpperCase() === guid
        && (u.is_system_admin === true || u.isSystemAdmin === true)
    );
  }, [currentUserGuid, apiSecurityUsers]);

  // DEV-1050 — V2-derived set of project GUIDs the current user can see.
  // Replaces securityEval.authorizedProjectGuids. Sysadmin sees every
  // effective project; non-sysadmin sees only projects where the V2
  // evaluator granted at least 'about:view' (the "any role here" floor).
  // Returns an empty set while V2 caps are still loading (the gate
  // functions adopt the same conservative-falsy posture).
  const v2VisibleProjectGuids = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    if (!currentUserGuid) return set;
    if (currentUserIsAdmin) {
      for (const p of effectiveProjects as any[]) {
        const guid = String(p?.project_GUID || p?.project_guid || p?.id || '').toUpperCase();
        if (guid) set.add(guid);
      }
      return set;
    }
    for (const [guid, caps] of v2_capsByProject.entries()) {
      if (v2CanViewProject(caps)) set.add(guid);
    }
    return set;
  }, [currentUserGuid, currentUserIsAdmin, effectiveProjects, v2_capsByProject]);

  // DEV-1050 — V2-derived authorized collection GUIDs. Collections aren't
  // part of V2's per-project capability model; the V1 logic was simply
  // "user is a member of this collection." That membership check is
  // reproduced here directly from apiCollectionMemberships. Sysadmin sees
  // every active collection.
  const authorizedCollectionGuids = useMemo<string[]>(() => {
    if (currentUserIsAdmin) {
      return ((apiCollections as any[]) || [])
        .map((c: any) => String(c?.collection_guid || ''))
        .filter(Boolean);
    }
    const ug = String(currentUserGuid || '').toUpperCase();
    if (!ug) return [];
    const seen = new Set<string>();
    for (const m of (apiCollectionMemberships as any[]) || []) {
      if (String(m.user_guid || '').toUpperCase() !== ug) continue;
      const cg = String(m.collection_guid || '');
      if (cg) seen.add(cg);
    }
    return Array.from(seen);
  }, [currentUserIsAdmin, currentUserGuid, apiCollections, apiCollectionMemberships]);

  // DEV-1050 — visible-projects filter set. Null sentinel preserves the
  // legacy "no filter yet — show all" semantics during V2 cold-start
  // (avoids the empty-list flash that would otherwise appear in the
  // ~500ms before V2 caps resolve). Once V2 has loaded, the set restricts
  // the list to authorized projects.
  const visibleProjectGuidSet = useMemo<Set<string> | null>(() => {
    if (!currentUserGuid) return null;
    if (v2RolesLoading) return null;
    return v2VisibleProjectGuids;
  }, [currentUserGuid, v2RolesLoading, v2VisibleProjectGuids]);

  const visibleProjects = useMemo(() => {
    if (!visibleProjectGuidSet) return effectiveProjects as any[];
    return (effectiveProjects as any[]).filter((p: any) =>
      visibleProjectGuidSet.has(String(p?.project_GUID).toUpperCase())
    );
  }, [effectiveProjects, visibleProjectGuidSet]);

  // DEV-839: Bulk-fetch HSE incidents for every visible project in one round
  // trip. The result is keyed by lowercase projectGuid and read inside the
  // Data memo below, where we attach `hse_bundle` to each project row so
  // the displayKpiState helper applies the floor cap on EVERY surface
  // (KpiGridTable, KpiSummaryHeader, etc.) — not just project detail.
  const visibleProjectGuidList = useMemo(() => {
    return (visibleProjects as any[])
      .map((p: any) => String(p?.project_GUID || '').trim())
      .filter(Boolean);
  }, [visibleProjects]);
  const { incidentsByProject: hseIncidentsByProject } = useHseIncidentsBulk(visibleProjectGuidList);

  // Hero-image lookup from the UNFILTERED project set.
  // Used by EngagementsPage to resolve thumbnails for projects the simulated
  // user has roles on but isn't authorized to see in the main project grid.
  const allProjectHeroLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of (effectiveProjects as any[]) || []) {
      const guid = String(p?.project_GUID ?? p?.project_guid ?? '').trim().toUpperCase();
      if (!guid) continue;
      const url = p.card_image_url || p.hero_image_url || p.tile_image_url || '';
      if (url) map.set(guid, url);
    }
    return map;
  }, [effectiveProjects]);

  const Data = useMemo(() => {
    // Intentional recompute triggers: these version counters are bumped by
    // data-save handlers (progress/budget entries) so the grid rebuilds with
    // fresh values even though the data is read via stable `api*` references.
    // `void` documents the intent and satisfies react-hooks/exhaustive-deps —
    // same pattern as the `void version;` memos elsewhere (DEV-1368 cleanup).
    void progressDataVersion;
    void budgetDataVersion;
    // Task #73 fix: use local-date components, not UTC. The naive
    // `new Date().toISOString().slice(0, 10)` returns UTC, which during
    // evening hours west of UTC (Americas) rolls forward to tomorrow's
    // date. With the wrong "today" value flowing into asOfDate, the
    // Data Reliability matrix's curve-based coverage treats a baseline
    // row for tomorrow as already due today, fabricating a missing
    // period. todayIsoLocal() returns the date in the user's timezone.
    const todayIso = todayIsoLocal();

    // Normalize GUIDs to uppercase for all lookups (CLAUDE.md: case-insensitive GUIDs).
    const progressByProject: Record<string, any[]> = {};
    for (const row of apiScheduleData as any[]) {
      const guid = String((row as any).project_GUID || '').trim().toUpperCase();
      if (!guid) continue;
      (progressByProject[guid] ||= []).push(row);
    }

    const missionByProject: Record<string, any[]> = {};
    for (const row of apiMissionData as any[]) {
      const guid = String((row as any).project_GUID || '').trim().toUpperCase();
      if (!guid) continue;
      (missionByProject[guid] ||= []).push(row);
    }

    const kpiByProjectGuid = new Map<string, any>();
    for (const pk of projectsKpi as any[]) {
      const g = String((pk as any)?.project_guid || '').trim().toUpperCase();
      if (g) kpiByProjectGuid.set(g, pk);
    }

    // DEV-851 (PR2 + per-instance refactor):
    // Per-instance state assembly — both the engine's `expected_instances`
    // (per-project ExpectedInstanceInput[]) and the engine's `progress_entries`
    // (flat ProgressEntryInput[] for Outlook signals) are built from the same
    // three production streams: apiScheduleData, apiFinanceData, missionRawPoints.
    //
    // The selector lives in src/Data/DataReliability/perInstanceState.ts so the
    // future ActionCenterPage (DEV-681 / DEV-526) can reuse the same logic
    // without duplication. Per-instance lateness classification (used by the
    // bell trigger DEV-529 + LeftNav badge) is exported from the same module.
    const reliabilityEntriesByProject = buildProgressEntriesByProject({
      scheduleRows: apiScheduleData as any[],
      financeRows: apiFinanceData as any[],
      missionPoints: missionRawPoints as any[],
    });

    const latestIndices = buildLatestIndices({
      scheduleRows: apiScheduleData as any[],
      financeRows: apiFinanceData as any[],
      missionPoints: missionRawPoints as any[],
    });

    // DEV-851 follow-up — all-reporting-dates indices feed the fractional
    // coverage computation (expected_periods / reported_periods / missing
    // _periods) on each ExpectedInstanceInput. Built once per render
    // because the math is shared across every project. Empty when streams
    // haven't loaded yet — coverage falls through to undefined and the
    // engine + matrix degrade gracefully to the legacy recency rule.
    const allDatesIndices = buildAllDatesIndices({
      scheduleRows: apiScheduleData as any[],
      financeRows: apiFinanceData as any[],
      missionPoints: missionRawPoints as any[],
    });

    // Task #72 — baseline-dates indices use ALL series-points rows (not
    // just rows with actuals), so coverage agrees with the data-entry
    // grid. Schedule and Finance use this; Mission has no baseline curve.
    // When a curve hasn't been generated yet for an instance, the
    // bucket-from-cadence fallback inside the selector kicks in.
    const baselineDatesIndices = buildBaselineDatesIndices({
      scheduleRows: apiScheduleData as any[],
      financeRows: apiFinanceData as any[],
      missionPoints: missionRawPoints as any[],
    });

    // Task #75 — submission-lag indices feed the matrix "Time" column.
    // Per-instance tuples of (reporting_date, created_at) used to compute
    // avg_lag_days = mean(max(0, created_at - reporting_date)) across
    // every reported point. Separate from the engine's headline timeliness
    // signal — this is row-level habit visibility.
    const lagIndices = buildLagIndices({
      scheduleRows: apiScheduleData as any[],
      financeRows: apiFinanceData as any[],
      missionPoints: missionRawPoints as any[],
    });

    /*
      DEV-681 Phase 2 — Action Center Data tab needs the latest schedule and
      finance points in their *full* camelCase API shape (not the seed shape),
      because ScheduleDataEntry / BudgetDataEntry render values from
      `actualComplete` / `actualSpend` / `baselineBudget`. We build a single
      lookup keyed by `${projectGuid}|${instanceGuid}` so per-row attachment
      is O(1) inside the loop below.

      `apiScheduleRaw` / `apiFinanceRaw` come from useApi*Data's `raw` field
      (the camelCase passthrough). Earliest reporting date wins; the latest
      reporting date wins per (project, instance) pair, matching the engine's
      classification rule.
    */
    const latestScheduleRawByProjectInstance: Record<
      string,
      ScheduleSeriesPointResponse
    > = {};
    for (const p of (apiScheduleRaw || []) as ScheduleSeriesPointResponse[]) {
      if (p.actualComplete == null) continue;
      const proj = String(p.projectId || '').toUpperCase();
      const inst = String(p.instanceGuid || '').toUpperCase();
      if (!proj || !inst) continue;
      const key = `${proj}|${inst}`;
      const existing = latestScheduleRawByProjectInstance[key];
      if (!existing || p.reportingDate > existing.reportingDate) {
        latestScheduleRawByProjectInstance[key] = p;
      }
    }
    const latestFinanceRawByProjectInstance: Record<
      string,
      BudgetSeriesPointResponse
    > = {};
    for (const p of (apiFinanceRaw || []) as BudgetSeriesPointResponse[]) {
      if (p.actualSpend == null) continue;
      const proj = String(p.projectId || '').toUpperCase();
      const inst = String(p.instanceGuid || '').toUpperCase();
      if (!proj || !inst) continue;
      const key = `${proj}|${inst}`;
      const existing = latestFinanceRawByProjectInstance[key];
      if (!existing || p.reportingDate > existing.reportingDate) {
        latestFinanceRawByProjectInstance[key] = p;
      }
    }

    // DEV-1096 / Task #207. Build the Full-Project rows the same way we
    // always have. Mini-Project rows are appended after the .map() below
    // with a minimal FP-shaped stub so the existing KpiGridTable + filter
    // chain can render them (and the polymorphic collection-membership
    // filter from Pass B can scope them to the selected collection).
    const fpRows = (visibleProjects as any[]).map((project: any) => {
      const projectGuid = String(project?.project_GUID || '').trim().toUpperCase();
      const pkSeed = kpiByProjectGuid.get(projectGuid) || {};
      const officialAsOf = (officialReportingCalendar as any)?.[projectGuid]?.last_closed_official_date ?? null;
      const asOfDate = kpiMode === 'official' && officialAsOf ? officialAsOf : todayIso;

      // ------------------------------------------------------------------
      // Build the progress series for this project.
      //
      // Priority order (per Architecture doc §11):
      //   1. localStorage override data (user-edited curves) — most current
      //   2. API-fetched data (when backend is live)
      //   3. Seed/fixture data (development fallback)
      //
      // During Phase 1, localStorage IS the source of truth for progress
      // data because the user edits curves via ScheduleThermometer which
      // writes to l4e_progress_series_v2.
      // ------------------------------------------------------------------
      let progressSeries: any[] = [];
      try {
        // Read per-instance progress data from localStorage.
        // If instances are configured, collect data from each instance.
        // Otherwise, try a project-level (no instance) read.
        const localCfg = getProjectKpiConfigOverride(projectGuid as any);
        const schedInstances: any[] = (localCfg as any)?.category_instances?.schedule || [];
        if (schedInstances.length > 0) {
          // Collect progress data across all schedule instances
          for (const inst of schedInstances) {
            const iGuid = String(inst?.instance_guid || '').trim();
            const instSeries = getScheduleProgressOverrideSeriesForInstance(projectGuid, iGuid || undefined);
            if (instSeries?.length) {
              for (const pt of instSeries) {
                progressSeries.push({
                  project_GUID: projectGuid,
                  instance_guid: iGuid || undefined,
                  reporting_date: pt.reporting_date,
                  baseline_complete: pt.baseline_complete,
                  actual_complete: pt.actual_complete,
                  forecast_complete: (pt as any).forecast_complete ?? null,
                });
              }
            }
          }
        } else {
          // No instances configured — try project-level localStorage read
          const projSeries = getScheduleProgressOverrideSeriesForInstance(projectGuid, undefined);
          if (projSeries?.length) {
            progressSeries = projSeries.map((pt) => ({
              project_GUID: projectGuid,
              reporting_date: pt.reporting_date,
              baseline_complete: pt.baseline_complete,
              actual_complete: pt.actual_complete,
              forecast_complete: (pt as any).forecast_complete ?? null,
            }));
          }
        }
      } catch { /* ignore localStorage errors */ }

      // Fallback: if localStorage had no data, use API/seed data
      if (progressSeries.length === 0) {
        progressSeries = (progressByProject[projectGuid] || []) as any[];
      }

      // Merge approved progress entries (workflow layer)
      try {
        const approved = getApprovedScheduleProgressForProject(projectGuid);
        if (approved?.length) {
          const byDate = new Map<string, any>();
          for (const r of progressSeries) {
            const d = String((r as any)?.reporting_date || (r as any)?.reportingDate || '');
            if (d) byDate.set(d, r);
          }
          const baseSorted = progressSeries.slice().sort((a, b) =>
            String((a as any)?.reporting_date || '').localeCompare(String((b as any)?.reporting_date || ''))
          );
          const getBaselineAtOrBefore = (ymd: string) => {
            let last = 0;
            for (const p of baseSorted) {
              const d = String((p as any)?.reporting_date || '');
              if (!d) continue;
              if (d <= ymd) last = Number((p as any)?.baseline_complete ?? last);
              else break;
            }
            return last;
          };
          for (const a of approved) {
            const d = String(a.reporting_date || '');
            if (!d) continue;
            const existing = byDate.get(d);
            if (existing) {
              byDate.set(d, { ...existing, actual_complete: Number(a.actual_complete) });
            } else {
              byDate.set(d, {
                project_GUID: projectGuid,
                reporting_date: d,
                baseline_complete: getBaselineAtOrBefore(d),
                actual_complete: Number(a.actual_complete),
              });
            }
          }
          progressSeries = Array.from(byDate.values()).sort((a, b) =>
            String((a as any)?.reporting_date || '').localeCompare(String((b as any)?.reporting_date || ''))
          );
        }
      } catch { /* ignore */ }

      const missionSeries = (missionByProject[projectGuid] || []) as any[];

      // Read mission instances directly from apiKpiConfigs (React state) rather
      // than getMissionInstancesFromStorage() which reads from kpiConfigStore
      // (a mutable Map populated by a useEffect that runs AFTER render).
      // Reading from state ensures data is available on the same render cycle.
      const missionInstances = (apiKpiConfigs.get(projectGuid)?.mission ?? []) as SubKpiInstance[];

      // Mission targets: prefer API-provided targets, fall back to targets
      // embedded in the instance config (set via KPI Configuration modal).
      const apiTargetsForProject = (apiMissionTargets as any)?.[projectGuid] ?? null;
      const instanceTargets: Record<string, any> = {};
      for (const inst of missionInstances) {
        const key = inst.outcome_id || inst.instance_guid || inst.display_name || "";
        if (key && (inst.target_goal !== undefined || inst.target_baseline !== undefined)) {
          instanceTargets[key] = {
            goal: inst.target_goal,
            baseline: inst.target_baseline,
            direction: inst.direction,
            milestone_total: inst.milestone_total,
          };
        }
      }
      const missionTargetsForProject = apiTargetsForProject
        ? { ...instanceTargets, ...apiTargetsForProject }
        : (Object.keys(instanceTargets).length > 0 ? instanceTargets : null);
      const missionPointsCount = missionSeries.length;
      const missionPointsSample = missionSeries.slice(0, 8);
      const dynamicMissionConfig = (missionInstances.length > 0)
        ? {
            subkpis: Object.fromEntries(
              missionInstances.map((inst) => {
                const key = inst.outcome_id || inst.instance_guid || inst.display_name || "unknown";
                // DEV-822: HSE-typed sub-KPIs (incident_count) are scored
                // by the parallel HSE pipeline (computeHseBundle in hse.ts),
                // NOT by the legacy Mission engine. Force their weight to 0
                // so they pass through the engine as tracked-only and don't
                // pollute the Mission rollup score with bogus numeric scoring
                // (their value_type is undefined → engine defaults to numeric).
                const isHseType = (inst as any).mission_type_key === 'incident_count';
                const effectiveWeight = isHseType ? 0 : (inst.weight ?? 1);
                return [key, {
                  weight: effectiveWeight,
                  cadence: inst.cadence || "monthly",
                  direction: inst.direction || "increase_is_good",
                  value_type: inst.value_type || "numeric",
                  outcome_id: inst.outcome_id,
                  aggregation: inst.tracking_mode === "cumulative" ? "cumulative" : "periodic",
                  missing_policy: inst.tracking_mode === "cumulative" ? undefined : "zero",
                  label: inst.display_name,
                  target_goal: inst.target_goal,
                  target_baseline: inst.target_baseline,
                  uom: inst.uom,
                  deadline_date: inst.deadline_date,
                  warning_days: inst.warning_days,
                  critical_days: inst.critical_days,
                  // DEV-814 / migration 122: per-instance color derivation mode.
                  // Engine resolves undefined to KPI_DEFAULTS per type.
                  color_mode: inst.color_mode,
                  // DEV-814: tracking start date grace period. Engine excludes
                  // the KPI from rollup if asOfDate < tracking_start_date.
                  tracking_start_date: inst.tracking_start_date,
                  // DEV-815: pace-mode tolerances (only used when color_mode='pace').
                  pace_yellow_units_behind: inst.pace_yellow_units_behind,
                  pace_red_units_behind: inst.pace_red_units_behind,
                  // DEV-816 / Skip: pass mission_type_config through so the
                  // Mission card can render type-specific detail (e.g., the
                  // Milestone Set's labeled list with check marks). Engine
                  // doesn't use it for scoring — pure pass-through to UI.
                  mission_type_config: (inst as any).mission_type_config,
                  // DEV-822: pass mission_type_key through so the UI layer
                  // can identify HSE-typed sub-KPIs (incident_count) and
                  // route them to the HSERow renderer instead of the
                  // regular Mission card list.
                  mission_type_key: (inst as any).mission_type_key,
                  // DEV-1075 Path A: scoring_mode discriminator. When
                  // 'qualitative' (Mini-Project tier), the engine reads
                  // .assessment from the latest point and applies the
                  // 5-state ladder. Undefined → defaults to 'numeric'.
                  scoring_mode: (inst as any).scoring_mode,
                  // DEV-1075: included_in_rollup. When explicitly false,
                  // suppress the row's contribution to the Mission
                  // weighted-average score while keeping its own
                  // indicator color visible. Replaces the weight=0
                  // workaround for administrative metrics.
                  included_in_rollup: (inst as any).included_in_rollup,
                }];
              })
            ),
          }
        : { subkpis: {} };  // Empty — no seed data fallback

      const effectiveConfig = { ...kpiConfig, mission: dynamicMissionConfig };


      const bundle = computeKpisForProject({
        projectGuid,
        mode: kpiMode,
        asOfDate,
        config: effectiveConfig as any,
        schedulePoints: progressSeries as any,
        missionPoints: missionSeries as any,
        missionTargets: missionTargetsForProject ?? undefined,
      });

      // Instance-aware Schedule scoring (Architecture doc §5: Layer 2 Category Rollup).
      //
      // Option A fix: The in-memory store is source of truth ONLY for the tab that
      // made a recent local edit. Remote tabs (receiving updates via SignalR) should
      // PREFER API data to avoid the stale-store race condition, but still fall back
      // Storage-first: the in-memory store is kept in sync across tabs via
      // BroadcastChannel (crossTabSync.ts), so it is always authoritative.
      // API data is a fallback for when the store doesn't have instance data.
      const storageResult = computeScheduleCategoryFromStorage(
        projectGuid,
        asOfDate,
        (kpiConfig as any).schedule,
      );
      const apiInstances = apiKpiConfigs.get(projectGuid)?.schedule;
      const apiSchedResult = (apiInstances && apiInstances.length > 0)
        ? computeScheduleCategoryFromData(
            projectGuid,
            asOfDate,
            (kpiConfig as any).schedule,
            apiInstances as any,
            apiScheduleData,
          )
        : null;
      const schedInstanceResult = storageResult ?? apiSchedResult;
      const schedSignal = schedInstanceResult?.signal ?? bundle.schedule;


      // Instance-aware Finance scoring.
      // Storage-first for finance too — BroadcastChannel keeps stores in sync.
      const apiFinanceInstances = apiKpiConfigs.get(projectGuid)?.finance;

      const financeStorageResult = computeFinanceCategoryFromStorage(
        projectGuid,
        asOfDate,
        (kpiConfig as any).finance,
      );

      const financeApiResult =
        apiFinanceInstances && apiFinanceInstances.length > 0 && apiFinanceData.length > 0
          ? computeFinanceCategoryFromData(
              projectGuid,
              asOfDate,
              (kpiConfig as any).finance,
              apiFinanceInstances as any,
              apiFinanceData,
            )
          : null;

      const financeInstanceResult = financeStorageResult ?? financeApiResult;

      // Fallback: project-level cost data (no instances needed).
      const storageFallback = computeFinanceFallbackFromStorage(projectGuid, asOfDate, (kpiConfig as any).finance);
      const apiFallback = apiFinanceData.length > 0
        ? computeFinanceFallbackFromData(projectGuid, asOfDate, (kpiConfig as any).finance, apiFinanceData)
        : null;
      const financeFallbackSignal = !financeInstanceResult
        ? (storageFallback || apiFallback)
        : null;
      const financeSignal = financeInstanceResult?.signal ?? financeFallbackSignal ?? null;

      // When no real finance data exists, show grey/no-data (color=0, score=null).
      const budgetScore = financeSignal ? financeSignal.score_0_100 : null;
      const financeTrend = (financeSignal ? financeSignal.indicator_trend_value : 0) as -1 | 0 | 1;
      const financeColor = financeSignal ? financeSignal.indicator_color_value : 0;

      // ── Data Reliability v2 (DEV-851) ─────────────────────────────────
      // Engine v2 (computeDataReliability) is the sole source of the
      // reliability score. The legacy computeReliabilityKpiSignal path was
      // retired in PR2 — bundle.data_reliability is now a null pass-through
      // and reliability_subkpis is gone. All consumers (tab, grid, map,
      // header) pull from data_reliability_now / data_reliability_outlook /
      // legacy_composite_score on the row.
      //
      // For v1, Outlook inputs are stubbed (digest_acks, preflight_items,
      // phase_summary, raci_assignment_count). The Outlook lifecycle gate
      // fails cleanly under stubs → applicable: false → Outlook renders
      // as N/A. Now (Completeness + Timeliness) populates fully from the
      // ProgressEntryInput stream assembled at the top of this useMemo.
      const projConfigOverride = getProjectOverride(projectGuid as any);
      const isCompletedProject = isProjectCompleted(project, projConfigOverride, todayIso);
      // DEV-681: Action Center surfaces (Data tab + DEV-529 bell) only act
      // on non-terminal projects. Proposed / Planned / In Progress all
      // qualify because legitimate reporting work happens at every
      // pre-execution phase too (feasibility data, pre-mobilization
      // preflight, baseline measurements). Only Abandoned and Completed
      // are filtered out. Distinct from `_isCompletedProject` because
      // that one also drives the blue Complete-state indicator on the
      // at-a-glance KPI surfaces.
      const isActiveProject = isProjectActive(project, projConfigOverride);

      const projStartDate = String(
        projConfigOverride?.project_start_date
        ?? (project as any)?.start_date
        ?? (project as any)?.project_start_date
        ?? (project as any)?.startDate
        ?? ""
      ).trim();
      const projEndDate = String(
        projConfigOverride?.project_end_date
        ?? (project as any)?.finish_date
        ?? (project as any)?.project_end_date
        ?? (project as any)?.end_date
        ?? ""
      ).trim();
      const projStatusForEngine = String(
        projConfigOverride?.project_status
        ?? (project as any)?.project_status
        ?? ""
      ).trim();

      // DEV-851 (per-instance refactor) — assemble via shared selector.
      // Same logic now lives in src/Data/DataReliability/perInstanceState.ts
      // so the future ActionCenterPage can read identical state without
      // duplicating the math. Selector is pure and testable in isolation.
      // DEV-963: apiKpiConfigs.get() returns GroupedKpiConfigs (schema-loose)
      // but selectors expect ProjectKpiConfig (schema-strict). Shapes overlap
      // at runtime; we cast at the boundary to silence TS without rewriting
      // the selectors. Migration to a single canonical type is tracked under
      // DEV-968 (entity-store consolidation).
      const projectKpiCfg = apiKpiConfigs.get(projectGuid) as any;
      const projectGraceDefault = (kpiConfig as any)?.data_reliability?.default_permissible_days_late ?? 2;
      const expectedInstances = buildExpectedInstancesForProject({
        projectGuid,
        kpiCfg: projectKpiCfg,
        latestIndices,
        // DEV-851 follow-up — fractional coverage. When all three are
        // present the selector populates expected_periods, reported_
        // periods, missing_periods on each ExpectedInstanceInput. The
        // engine's completeness component reads those for its score; the
        // DataReliabilityTab matrix reads them for its Missing column +
        // tighter Status taxonomy.
        allDatesIndices,
        // Task #72 — Schedule + Finance prefer the baseline curve over
        // cadence-bucket math so the matrix agrees with the user's
        // data-entry grid (no phantom "missing" periods when the curve
        // itself skipped a date). Mission falls back to cadence buckets
        // because mission has no baseline curve.
        baselineDatesIndices,
        // Task #75 — per-instance submission-lag tuples for the new
        // Time column on the matrix.
        lagIndices,
        projectStartDate: projStartDate || null,
        asOfDate,
      });
      // DEV-681: human label lookup for the Action Center Data tab. Keyed by
      // the same instance_id strings ExpectedInstanceInput carries, so the
      // tab can render display_name for each actionable item without
      // re-reaching into apiKpiConfigs itself.
      const expectedInstanceLabels = buildInstanceLabelsForProject(projectKpiCfg);

      /*
        DEV-681 Phase 2 — extra fields the Data tab needs to compose
        ScheduleDataEntry / BudgetDataEntry / MissionDataEntry per-row:
          - scheduleInstances / financeInstances: SubKpiInstance[] from the
            project's KPI config. Mission instances are already on the row
            via mission_subkpis (used by the existing Mission tab too).
          - latestSchedule/FinancePointsByInstance: keyed by instance_guid
            (UPPER), ready for the wrapper's Current column.
          - canEditKpiData: per-project permission gate. Action Center
            shows only projects where the user can act.
      */
      const scheduleInstances = projectKpiCfg?.schedule || [];
      const financeInstances = projectKpiCfg?.finance || [];

      const latestSchedulePointsByInstance: Record<
        string,
        ScheduleSeriesPointResponse
      > = {};
      for (const inst of scheduleInstances) {
        const ig = String(inst?.instance_guid || '').toUpperCase();
        if (!ig) continue;
        const found = latestScheduleRawByProjectInstance[`${projectGuid}|${ig}`];
        if (found) latestSchedulePointsByInstance[ig] = found;
      }
      const latestFinancePointsByInstance: Record<
        string,
        BudgetSeriesPointResponse
      > = {};
      for (const inst of financeInstances) {
        const ig = String(inst?.instance_guid || '').toUpperCase();
        if (!ig) continue;
        const found = latestFinanceRawByProjectInstance[`${projectGuid}|${ig}`];
        if (found) latestFinancePointsByInstance[ig] = found;
      }

      const canEditKpiData = !!(
        currentUserGuid && canEditProjectContent(currentUserGuid, projectGuid)
      );

      const dataReliabilityInputs: DataReliabilityInputs = {
        project_guid: projectGuid,
        as_of_date: asOfDate,
        project_metadata: {
          status: projStatusForEngine,
          start_date: projStartDate || null,
          end_date: projEndDate || null,
          // STUB v1: Outlook lifecycle gate fails when 0 → Outlook = N/A.
          // DEV-851e backend ticket adds raci_assignment_count to project DTO.
          raci_assignment_count: 0,
        },
        progress_entries: reliabilityEntriesByProject[projectGuid] || [],
        // DEV-851: expected_instances drives per-instance Completeness +
        // Timeliness. Empty array → engine falls back to per-category logic
        // (legacy path used by tests).
        expected_instances: expectedInstances,
        reporting_cadence: toEngineCadenceInput(resolveReportingCadence(projConfigOverride)),
        grace_days: projectGraceDefault,
        // STUB v1: Outlook signals — Engagement, PreFlightRisk, DriftRisk all
        // pass empty inputs. Outlook composite gate handles N/A cleanly.
        digest_acks: [],
        preflight_items: [],
        phase_summary: { phase_count: 0, warning_count: 0, critical_count: 0 },
        // DEV-849 will surface a per-project override; for now defaults are sufficient.
        weights: DEFAULT_DATA_RELIABILITY_WEIGHTS,
      };

      const reliabilityResult = computeDataReliability(dataReliabilityInputs);

      // Map engine output onto the legacy field names consumed by the five
      // at-a-glance surfaces. legacy_composite_score prefers Now → Outlook → null,
      // matching the existing single-composite UX. Completed projects produce
      // null here (Now's lifecycle gate fails); the consumer surfaces apply
      // the blue Complete-state override using `_isCompletedProject`.
      const reliabilityScore = reliabilityResult.legacy_composite_score;
      const reliabilityColor = reliabilityResult.legacy_composite_color;

      // Recompute Summary using instance-based schedule + finance scores,
      // and the new engine's summary_score (Now-only per DEV-846 lock).
      const hasSummaryOverride = !!(schedInstanceResult || financeInstanceResult || financeFallbackSignal);
      const summarySignal = hasSummaryOverride
        ? computeSummaryKpiSignal(asOfDate, (kpiConfig as any).summary, {
            schedule: { score: schedSignal.score_0_100, trend: schedSignal.indicator_trend_value },
            finance: { score: budgetScore, trend: financeTrend },
            mission: { score: bundle.mission.score_0_100, trend: bundle.mission.indicator_trend_value },
            data_reliability: {
              score: reliabilityResult.summary_score,
              trend: reliabilityResult.summary_trend,
            },
          })
        : bundle.summary;

      // (Removed 2026-05-11 — Skip flagged as noise. The diagnostic was
      // useful during the cross-browser KPI rollout but fires once per
      // project per render now that the engine is stable. Re-add behind
      // a debugMode gate if a regression needs the trace.)

      // DEV-839: Compute hse_bundle for THIS project from the bulk-fetched
      // incidents + the row's own HSE config sub-KPIs (mission_subkpis filtered
      // by mission_type_key='incident_count'). Pure function — same shape as
      // KpiNavigation's local compute, so cross-surface parity holds.
      const hseConfigsForProject = (bundle.mission_subkpis || [])
        .filter((sk: any) => sk?.mission_type_key === 'incident_count')
        .map((sk: any) => ({
          sub_kpi_guid: String(sk?.sub_kpi_guid || sk?.subKpiKey || ''),
          config: (sk?.mission_type_config ?? {
            display_name: sk?.label ?? 'HSE',
            hse_category: 'health',
            display_mode: 'streak',
            period: 'quarter',
          }) as IncidentCountConfig,
        }));
      const incidentsForProject =
        hseIncidentsByProject.get(projectGuid.toLowerCase()) ?? [];
      const hseBundleForRow = hseConfigsForProject.length > 0
        ? computeHseBundle(hseConfigsForProject, incidentsForProject, asOfDate)
        : null;

      return {
        ...pkSeed,
        finance_indicator_color: financeColor,
        finance_indicator_trend: financeTrend,
        finance_score_0_100: budgetScore,
        finance_as_of_date: financeSignal?.as_of_date ?? asOfDate,
        finance_explain: financeSignal?.explain ?? null,
        finance_instance_scores: financeInstanceResult?.instanceScores ?? null,
        project_guid: projectGuid,
        schedule_indicator_color: schedSignal.indicator_color_value,
        schedule_indicator_trend: schedSignal.indicator_trend_value,
        mission_indicator_color: bundle.mission.indicator_color_value,
        mission_indicator_trend: bundle.mission.indicator_trend_value,
        data_reliability_color: reliabilityColor,
        // DEV-851: trend now sourced from engine v2 (Now's trend, since Summary uses Now only).
        // For not-yet-applicable Now (new project, completed), trend defaults to 0.
        data_reliability_trend: reliabilityResult.now.applicable
          ? reliabilityResult.now.trend
          : 0,
        summary_indicator_color: summarySignal.indicator_color_value,
        summary_indicator_trend: summarySignal.indicator_trend_value,
        schedule_score_0_100: schedSignal.score_0_100,
        schedule_as_of_date: schedSignal.as_of_date,
        schedule_explain: schedSignal.explain,
        schedule_instance_scores: schedInstanceResult?.instanceScores ?? null,
        mission_score_0_100: bundle.mission.score_0_100,
        mission_as_of_date: bundle.mission.as_of_date,
        mission_explain: bundle.mission.explain,
        mission_subkpis: bundle.mission_subkpis,
        // DEV-839: Read by displayKpiState helpers (getMissionDisplayState /
        // getSummaryDisplayState) on every surface that renders Mission or
        // Summary color. Null when the project has no HSE config.
        hse_bundle: hseBundleForRow,
        kpi_mode: kpiMode,
        kpi_as_of_date: asOfDate,
        mission_data: missionSeries,
        mission_raw_points: (missionRawPoints || []).filter(
          (p) => String(p.projectId).toUpperCase() === String(projectGuid).toUpperCase()
        ),
        mission_points_count: missionPointsCount,
        mission_points_sample: missionPointsSample,
        mission_targets: missionTargetsForProject,
        data_reliability_score_0_100: reliabilityScore,
        data_reliability_as_of_date: reliabilityResult.as_of_date,
        // DEV-851 (PR2): legacy data_reliability_explain / data_reliability_subkpis
        // fields removed. The redesigned DataReliabilityTab now consumes the
        // structured `data_reliability_now` and `data_reliability_outlook`
        // composites below; KpiNavigation debug textareas dump them too.
        data_reliability_now: reliabilityResult.now,
        data_reliability_outlook: reliabilityResult.outlook,
        // DEV-681 (Action Center): the per-instance expected list is exactly
        // what the Action Center Data tab needs to filter to actionable rows.
        // Attaching it here means the tab stays a pure consumer — no need
        // to re-derive the same data from raw API streams.
        data_reliability_expected_instances: expectedInstances,
        data_reliability_instance_labels: expectedInstanceLabels,
        // DEV-681 Phase 2 — Data tab inputs. The tab composes
        // ScheduleDataEntry / BudgetDataEntry / MissionDataEntry per project.
        // Each wrapper accepts an instances list + latest-points-by-instance
        // map; we attach both here so the tab is a thin renderer.
        action_center_schedule_instances: scheduleInstances,
        action_center_finance_instances: financeInstances,
        action_center_latest_schedule_points: latestSchedulePointsByInstance,
        action_center_latest_finance_points: latestFinancePointsByInstance,
        action_center_can_edit_kpi: canEditKpiData,
        summary_score_0_100: summarySignal.score_0_100,
        summary_as_of_date: summarySignal.as_of_date,
        summary_explain: summarySignal.explain,
        project,
        _isCompletedProject: isCompletedProject,
        _isActiveProject: isActiveProject,
      };
    });

    // DEV-1096 / Task #207. Append Mini-Project rows. The shape is a slim
    // FP-compatible stub — enough for the KpiGridTable to render them by
    // title and for the polymorphic membership filter to scope them when a
    // collection is selected. KPI scoring fields are left null/0 because
    // MP KPI scoring is computed via a different pipeline (DEV-1116) that
    // isn't wired into this aggregator yet.
    //
    // `_isMiniProject: true` lets downstream consumers branch on row type:
    //   • ProjectMap currently filters MP rows out (no lat/lon plumbing).
    //   • KpiGridTable renders them in the table (the score columns just
    //     show grey because the score values are null).
    //
    // Archived MPs are excluded so the grid doesn't show defunct work.
    // DEV-1116 Phase 1b. Index the bulk portfolio-signals payload by MP
    // guid (case-insensitive on both sides) so the per-row scoring call
    // below is O(1). Empty map if the bulk fetch hasn't resolved or
    // errored — coloring falls back to Phase 1a semantics in that case.
    const mpSignalsByGuid = new Map<string, typeof apiMpPortfolioSignals[number]>();
    for (const row of (apiMpPortfolioSignals || [])) {
      if (row?.miniProjectGuid) {
        mpSignalsByGuid.set(String(row.miniProjectGuid).toUpperCase(), row);
      }
    }

    // Task #214. MP Geography for portfolio grouping.
    //
    // KpiGridTable's "Group by Geography" reads row.project.community_l1/l2/l3
    // off each row. FPs get those fields populated by rederiveCommunityFields
    // upstream of effectiveProjects (App.tsx:1619). MPs were appended AFTER
    // that, so their nested project object had no community_l1/l2/l3 and
    // every MP fell into the "(none)" geography bucket.
    //
    // Fix: build a synthetic project-shaped object per MP whose
    // primary_location_entity_guid is `mp.locationGeographyGuid` (when the
    // Lead pinned a specific location) falling back to `mp.ownerCommunityGuid`
    // (the community that owns the MP — same fallback the rest of the MP
    // header/breadcrumb code uses). Then run the SAME walker FPs use so the
    // results agree by construction. Indexed by upper-cased MP guid so the
    // per-row attachment below is O(1).
    const mpGeoByGuid = new Map<string, { community_l1: string; community_l2: string; community_l3: string; Community: string }>();
    {
      const seeds = (apiMiniProjects || [])
        // DEV-1210 — Filter on archivedAt (the real archive signal). The old
      // `mp.status !== 'archived'` test was dead code: MiniProject.status is
      // typed 'draft' | 'active' | 'complete' | 'expired' and never carries
      // 'archived'. Archive state lives in mp.archivedAt only.
      .filter((mp) => !mp.archivedAt && !!mp.miniProjectGuid)
        .map((mp) => ({
          __mpGuid: String(mp.miniProjectGuid).toUpperCase(),
          primary_location_entity_guid: mp.locationGeographyGuid || mp.ownerCommunityGuid,
        }));
      const derived = rederiveCommunityFields(seeds);
      for (const d of derived) {
        const k = String((d as any).__mpGuid || '');
        if (!k) continue;
        mpGeoByGuid.set(k, {
          community_l1: String((d as any).community_l1 || ''),
          community_l2: String((d as any).community_l2 || ''),
          community_l3: String((d as any).community_l3 || ''),
          Community: String((d as any).Community || ''),
        });
      }
    }

    const mpRows = (apiMiniProjects || [])
      // DEV-1210 — Filter on archivedAt (the real archive signal). The old
      // `mp.status !== 'archived'` test was dead code: MiniProject.status is
      // typed 'draft' | 'active' | 'complete' | 'expired' and never carries
      // 'archived'. Archive state lives in mp.archivedAt only.
      .filter((mp) => !mp.archivedAt && !!mp.miniProjectGuid)
      .map((mp) => {
        const mpGuid = String(mp.miniProjectGuid).toUpperCase();
        // DEV-1116 Phase 1b. Pull the slim per-MP signal inputs from the
        // bulk fetch and synthesize the shapes computeMiniProjectKpiStatus
        // expects:
        //   • latestStatus: MiniProjectStatus | null — only plannedPct /
        //     actualPct / reportingDate are read by the function's
        //     Schedule classifier. Other fields stub-defaulted.
        //   • goals[]: only `latestAssessment` and `includedInRollup` are
        //     read by the function's Mission aggregator. Other fields
        //     stub-defaulted to satisfy the type.
        // When the bulk fetch is empty (loading or errored), both fall
        // back to [] / null and the function degrades to Phase 1a output.
        const signals = mpSignalsByGuid.get(mpGuid);
        const latestStatus = signals?.latestStatus
          ? ({
              plannedPct: signals.latestStatus.plannedPct,
              actualPct: signals.latestStatus.actualPct,
              reportingDate: signals.latestStatus.reportingDate,
              // Migration 190 — Budget KPI dot reads this. Without it,
              // computeMiniProjectKpiStatus falls back to grey and the
              // portfolio grid stays out of sync with the detail page.
              budgetAssessment: signals.latestStatus.budgetAssessment ?? null,
              // Stub fields the function doesn't read but the type requires.
              miniProjectGuid: mp.miniProjectGuid,
              statusGuid: '',
              narrative: '',
              postedByUserGuid: '',
              postedAt: '',
            } as any)
          : null;
        const goalsForFn = (signals?.goals || []).map((g) => ({
          // Only `latestAssessment` and `includedInRollup` are read by
          // the Mission aggregator. Other fields cast-stubbed.
          latestAssessment: g.latestAssessment,
          includedInRollup: g.includedInRollup,
          goalGuid: g.goalGuid,
        } as any));
        const kpi = computeMiniProjectKpiStatus(mp, goalsForFn, [], [], latestStatus);
        // Bug A — KPI trend arrows for MP rows on the portfolio grid.
        // Reuses computeMpKpiTrends (the SAME helper the MP detail page's
        // ProgressTab calls) so grid and detail page agree by construction.
        // Schedule/Budget compare the latest vs previous status summaries;
        // Mission compares per-goal assessments as of each status's date.
        // The helper's aggregateMissionColor only reads goalAssessments[],
        // so lightweight stand-ins for the "full status" args suffice.
        // First-status case (no previousStatus from the signals endpoint)
        // → all trends null → mpTrendToCode → 0 → circles. DR is left
        // unset (0 → circle) per Skip's 2026-05-27 lock — no snapshot
        // infrastructure to trend it against.
        const kpiTrend =
          signals?.latestStatus && signals?.previousStatus
            ? computeMpKpiTrends({
                latestSummary: {
                  plannedPct: signals.latestStatus.plannedPct,
                  actualPct: signals.latestStatus.actualPct,
                  budgetAssessment: (signals.latestStatus.budgetAssessment ?? null) as any,
                },
                previousSummary: {
                  plannedPct: signals.previousStatus.plannedPct,
                  actualPct: signals.previousStatus.actualPct,
                  budgetAssessment: (signals.previousStatus.budgetAssessment ?? null) as any,
                },
                latestFull: {
                  goalAssessments: (signals.goals || [])
                    .filter((g) => g.latestAssessment != null)
                    .map((g) => ({ goalGuid: g.goalGuid, assessment: g.latestAssessment as string })),
                },
                previousFull: {
                  goalAssessments: (signals.goals || [])
                    .filter((g) => g.previousAssessment != null)
                    .map((g) => ({ goalGuid: g.goalGuid, assessment: g.previousAssessment as string })),
                },
                durationDays: projectDurationDays(mp.startDate, mp.endDate),
              })
            : null;
        // Diagnostic trace (gated on debugMode per CLAUDE.md). Lets you
        // verify the slim portfolio-signals payload is delivering
        // budgetAssessment and the grid's Budget dot color matches.
        // Remove or downgrade to a permanent debug surface once the
        // sync issue is confirmed resolved.
        if (debugMode) {
          // eslint-disable-next-line no-console
          console.log("[mpKpiAgg]", {
            mp: mp.title,
            mpGuid: mp.miniProjectGuid,
            hasSignals: !!signals,
            hasLatestStatus: !!signals?.latestStatus,
            budgetAssessment: signals?.latestStatus?.budgetAssessment ?? "(none)",
            plannedPct: signals?.latestStatus?.plannedPct,
            actualPct: signals?.latestStatus?.actualPct,
            computedSchedule: kpi.schedule,
            computedBudget: kpi.budget,
            financeIndicatorCode: mpColorToCode(kpi.budget),
          });
        }
        return {
          // Identity — uppercased so the membership filter (which adds
          // both project and mini-project guids in uppercase via the seed
          // shape) matches this row.
          project_GUID: mpGuid,
          project_guid: mpGuid,
          project_name: mp.title,
          project_status: mp.projectStatus || mp.status || '',
          project_category: mp.categoryKey || '',
          content_status: mp.contentStatus || '',
          start_date: mp.startDate || '',
          finish_date: mp.endDate || '',
          project_start_date: mp.startDate || '',
          project_end_date: mp.endDate || '',
          visibility: mp.visibility || 'community',
          // Migration 191 — card_image_url is now an independent column
          // on mini_projects. Card prefers it; falls back to hero when null.
          card_image_url: mp.cardImageUrl || mp.heroImageUrl || '',
          hero_image_url: mp.heroImageUrl || '',
          tile_image_url: mp.cardImageUrl || mp.heroImageUrl || '',
          location_lat: mp.locationLat ?? '',
          location_lon: mp.locationLon ?? '',
          // DEV-1116. Color signals via computeMiniProjectKpiStatus → numeric
          // codes (0..4) that match what the FP engine emits on the same
          // `*_indicator_color` field names. Critical: the grid does
          // `Number(colorRaw)` on these — strings (hex) silently route to 0.
          // Numeric scores stay null; the grid prefers score_0_100 when
          // available but falls through to the color code, which is the
          // path MP rows take until per-MP scoring lands.
          schedule_indicator_color: mpColorToCode(kpi.schedule),
          // Bug A — trend arrows. snapshotFromRow (KpiGridTable) reads
          // `${field}_indicator_trend` via num(): 1 = up arrow, -1 = down
          // arrow, 0 = circle. Same encoding the FP kpi-engine emits.
          schedule_indicator_trend: mpTrendToCode(kpiTrend?.schedule ?? null),
          schedule_score_0_100: null,
          schedule_as_of_date: null,
          schedule_explain: null,
          mission_indicator_color: mpColorToCode(kpi.mission),
          mission_indicator_trend: mpTrendToCode(kpiTrend?.mission ?? null),
          mission_score_0_100: null,
          mission_as_of_date: null,
          mission_explain: null,
          mission_subkpis: [],
          mission_data: [],
          mission_raw_points: [],
          mission_points_count: 0,
          mission_targets: [],
          finance_indicator_color: mpColorToCode(kpi.budget),
          // Bug A — the grid calls Budget "finance" on row field names.
          finance_indicator_trend: mpTrendToCode(kpiTrend?.budget ?? null),
          finance_score_0_100: null,
          finance_as_of_date: null,
          finance_explain: null,
          data_reliability_color: mpColorToCode(kpi.dataReliability),
          data_reliability_score_0_100: null,
          data_reliability_now: null,
          data_reliability_outlook: null,
          summary_indicator_color: mpColorToCode(kpi.summary),
          summary_indicator_trend: mpTrendToCode(kpiTrend?.summary ?? null),
          summary_score_0_100: null,
          summary_as_of_date: null,
          summary_explain: null,
          hse_bundle: null,
          kpi_mode: kpiMode,
          kpi_as_of_date: todayIso,
          // Stubs for action-center-related fields so consumers don't NPE.
          action_center_schedule_instances: [],
          action_center_finance_instances: [],
          action_center_latest_schedule_points: {},
          action_center_latest_finance_points: {},
          action_center_can_edit_kpi: false,
          schedule_instance_scores: null,
          data_reliability_expected_instances: [],
          data_reliability_instance_labels: {},
          // Owner-community echo so existing community-grouping logic has
          // something to read against MP rows.
          owner_community_guid: String(mp.ownerCommunityGuid || '').toUpperCase(),
          // Discriminator + raw payload for any downstream code that needs
          // the full MP detail.
          _isMiniProject: true,
          _isCompletedProject: mp.status === 'complete',
          _isActiveProject: mp.status === 'active',
          // The grid reads `item.project.project_name` (snake_case) from
          // this nested object. MPs come from the API in camelCase, so we
          // build a synthetic FP-shaped project object that maps the MP
          // fields onto the snake_case names the grid expects. The raw
          // MiniProject is also spread so consumers that want the original
          // camelCase fields (title, status, etc.) still see them.
          project: {
            ...mp,
            project_GUID: mpGuid,
            project_guid: mpGuid,
            project_name: mp.title,
            // DEV-1271 (Skip 2026-06-08): the LIFECYCLE status — not the old
            // soft `projectStatus` label — is the canonical status for
            // Mini-Projects. Synthesize the FP-shaped `project_status` purely
            // from the lifecycle via the shared map so every downstream
            // consumer (KPI grid grouping, portfolio sort, search, the
            // isProjectActive/isProjectCompleted predicates, the
            // _isCompletedProject blue override) keys off the lifecycle
            // automatically. Falls back to the raw lifecycle value if the
            // map ever misses. The soft `projectStatus` no longer drives any
            // sort/group/search logic.
            project_status: miniLifecycleToProjectStatus(mp.status) || mp.status || '',
            // Task #214. Surface the location anchor on the nested project
            // so any downstream code that walks primary_location_entity_guid
            // for an MP (other than the grid, which reads community_l1/l2/l3
            // below) Just Works.
            primary_location_entity_guid: mp.locationGeographyGuid || mp.ownerCommunityGuid,
            // Task #214. Geography fields for KpiGridTable Group By.
            // Empty strings when no anchor / no path resolves — matches the
            // FP shape when a project hasn't set a primary location yet.
            community_l1: mpGeoByGuid.get(mpGuid)?.community_l1 ?? '',
            community_l2: mpGeoByGuid.get(mpGuid)?.community_l2 ?? '',
            community_l3: mpGeoByGuid.get(mpGuid)?.community_l3 ?? '',
            Community: mpGeoByGuid.get(mpGuid)?.Community ?? '',
            project_category: mp.categoryKey || '',
            content_status: mp.contentStatus || '',
            start_date: mp.startDate || '',
            finish_date: mp.endDate || '',
            project_start_date: mp.startDate || '',
            project_end_date: mp.endDate || '',
            visibility: mp.visibility || 'community',
            // Migration 191 — same pattern as the top-level block above.
            // ProjectCard reads from this nested `project.*` shape, so the
            // card prefers cardImageUrl with a fallback to hero. Without
            // this mirror, the DB write persists but the displayed card
            // keeps showing the hero because the nested shape is stale.
            card_image_url: mp.cardImageUrl || mp.heroImageUrl || '',
            hero_image_url: mp.heroImageUrl || '',
            tile_image_url: mp.cardImageUrl || mp.heroImageUrl || '',
            location_lat: mp.locationLat ?? '',
            location_lon: mp.locationLon ?? '',
            owner_community_guid: String(mp.ownerCommunityGuid || '').toUpperCase(),
            _isMiniProject: true,
          },
        };
      });

    return [...fpRows, ...mpRows];
  }, [kpiMode, visibleProjects, apiScheduleData, apiScheduleRaw, apiFinanceData, apiFinanceRaw, apiKpiConfigs, apiMissionData, apiMissionTargets, missionRawPoints, progressDataVersion, budgetDataVersion, debugMode, hseIncidentsByProject, currentUserGuid, apiMiniProjects, apiMpPortfolioSignals]);

  // ── Action Center badge (§7.14, Track 4 Slice A) ──────────────────
  // ONE shared composer (same hook the inbox consumes) + the shared
  // seen/resolved store, so the LeftNav badge counts exactly the unviewed
  // subset of the SAME item set the inbox renders — count and list can never
  // diverge. Badge mode composes RACI from the lightweight /raci-open ID-list
  // instead of the inbox's per-project Gantt fan-out (lazy-source audit).
  // Replaces the old approvalsBadge + onboardingBadge + staleGuidelinesCount
  // triple, whose terms now ride inside the one composed set.
  const { items: actionCenterItems, engagementEntries: actionCenterEngagementEntries } =
    useActionItemComposer({
    mode: 'badge',
    currentUserGuid,
    users: apiSecurityUsers,
    projects: apiProjects,
    communities: apiCommunityEntities,
    relationships: apiCommunityRelationships,
    data: Data,
    communityMemberships: apiCommunityMemberships,
    projectAcl: apiProjectAcl,
    allProjectHeroLookup,
    communityRoleAssignments: (apiCommunityRoles as any[]) || [],
  });
  const { seenIds: acSeenIds, resolvedIds: acResolvedIds } =
    useActionItemSeenStore(currentUserGuid);
  const actionCenterBadge = useMemo(
    () =>
      actionCenterItems.filter((it) =>
        isUnseenActionItem(it, { seenIds: acSeenIds, resolvedIds: acResolvedIds }),
      ).length,
    [actionCenterItems, acSeenIds, acResolvedIds],
  );

  // ── Current user avatar info (for top-bar) ──
  const currentUserInfo = useMemo(() => {
    const guid = currentUserGuid.toUpperCase();
    const apiUser = (apiSecurityUsers || []).find(
      (u: any) => String(u.user_guid || u.userGuid || '').toUpperCase() === guid
    );
    const displayName = String(apiUser?.display_name || apiUser?.displayName || apiUser?.name || '');
    const profileImg = mediaSrc(apiUser?.profile_image_url || apiUser?.profileImageUrl) || '';
    const initials = displayName
      ? displayName.split(/\s+/).map((w: string) => w.charAt(0).toUpperCase()).slice(0, 2).join('')
      : '?';
    // Binary avatar ring (Skip ruling 2026-07-08, DEV-1437): blue = rung
    // BASICS done (profile + follows + current EULA + standards), white =
    // passive — regardless of appointed archetype. One shared predicate
    // (ringStatus.hasBlueRing) mirrors the server rung gate. Users are
    // never LABELED "Enablers" (POLICY_ENABLER_VOCABULARY); the prop name
    // is a code identifier.
    const isEnabler = hasBlueRing(apiUser);
    return { displayName, profileImg, initials, isEnabler };
  }, [currentUserGuid, apiSecurityUsers]);

  // ── DEV-1019 / DEV-1372 / DEV-1413 — Current user's landing community
  // GUID, used by LeftNav as the fallback target when "Communities" is
  // clicked and no explicit Default Community View preference is set.
  //
  // DEV-1372 slice 1: the DECLARED community is the active type='home'
  // community_memberships row (the single source of truth).
  // DEV-1413 slice 2: the declared community may be INACTIVE (dormant
  // membership, §8.4 ruling 1) — landing resolves at runtime through the
  // landing walk (declared → nearest active ancestor → top active root).
  // The resolved ancestor is never written back as the membership.
  const currentUserHomeCommunityGuid = useMemo<string | null>(() => {
    const guid = currentUserGuid.toUpperCase();
    if (!guid) return null;
    let declared: string | null = null;
    for (const m of apiCommunityMemberships || []) {
      if (
        String((m as any).user_guid || '').toUpperCase() === guid &&
        String((m as any).membership_type || '').toLowerCase() === 'home' &&
        String((m as any).status || 'active').toLowerCase() === 'active'
      ) {
        const g = String((m as any).community_entity_guid || '').trim();
        if (g) { declared = g; break; }
      }
    }
    if (!declared) return null;
    const resolve = createLandingResolver(
      apiCommunityEntities as any,
      apiCommunityRelationships as any,
    );
    return resolve(declared);
  }, [currentUserGuid, apiCommunityMemberships, apiCommunityEntities, apiCommunityRelationships]);

  // ── DEV-1448 — the ONE default-community landing resolver (Skip ruling
  //    2026-07-09, §7.6): explicit server-backed pref → inviting org →
  //    home-geo landing walk → browse page. Consumed by BOTH entry points
  //    (the "/" post-auth landing and the My Impact nav click) so they can
  //    never diverge. Stale prefs fall through inside the resolver.
  const currentUserDefaultCommunityPref = useMemo<string | null>(() => {
    const guid = currentUserGuid.toUpperCase();
    if (!guid) return null;
    const row: any = (securityUsers || []).find(
      (u: any) => String(u?.user_guid || '').toUpperCase() === guid,
    );
    const v = row?.default_community_view;
    return v && String(v).trim() ? String(v) : null;
  }, [currentUserGuid, securityUsers]);

  // Keep the per-device localStorage cache reconciled with the server value
  // (localStorage demoted to optimistic cache — DEV-1443 cross-device lesson).
  useEffect(() => {
    if (!currentUserGuid || !(securityUsers || []).length) return;
    syncDefaultCommunityViewCache(currentUserDefaultCommunityPref, currentUserGuid);
  }, [currentUserGuid, currentUserDefaultCommunityPref, securityUsers]);

  // The "/" redirect must WAIT for the resolver's inputs — firing on the
  // first render (empty entities/users/onboarding) would resolve every
  // login to the browse page before the real data arrives.
  const landingResolverReady =
    (apiCommunityEntities?.length ?? 0) > 0 &&
    (securityUsers?.length ?? 0) > 0 &&
    !onboardingLoading;

  // §7.9 Start Page — the resolution carries the route AND the stale-pref
  // report (§7.9.9): when the saved community is gone/dissolved and we land
  // elsewhere, StartPageNotice tells the user once instead of a silent swap.
  const startPageResolution = useMemo(
    () =>
      resolveStartPage({
        pref: currentUserDefaultCommunityPref,
        invitedCommunityGuid: onboardingInvitedCommunityGuid,
        homeCommunityGuid: currentUserHomeCommunityGuid,
        entities: apiCommunityEntities as any,
        relationships: apiCommunityRelationships as any,
      }),
    [
      currentUserDefaultCommunityPref,
      onboardingInvitedCommunityGuid,
      currentUserHomeCommunityGuid,
      apiCommunityEntities,
      apiCommunityRelationships,
    ],
  );
  const communitiesLandingRoute = startPageResolution.route;

  /* DEV-1458 §7.10 — is the user AT their resolved Home? Home is an ANCHOR: it
     lights ONLY on the default start page, never on other communities. Computed
     here, in App, because only here do the resolvers live — and a naive path
     match against the resolved route would go dark the moment CommunityDetailRoute
     rewrites the GUID URL to its canonical slug. resolveCommunityFromPath resolves
     the slug back to the entity, so the comparison holds across the rewrite.

     The in-section mirror (/memberships/community/:guid) is deliberately NOT Home
     — that surface belongs to My Impact, which lights there instead. Home is the
     STANDALONE world page. */
  const homeActive = useMemo(() => {
    const route = communitiesLandingRoute;
    const path = location.pathname;

    // Surface-route home (an explicit /discover, /actions, /memberships, or
    // /communities-browse pref): exact pathname match.
    if (!route.startsWith('/communities/')) {
      return path === route;
    }

    // A community home. The in-section mirror is My Impact's, not Home's.
    if (path.startsWith('/memberships')) return false;

    const homeGuid = route.split('/')[2]?.toUpperCase() || null;
    const cur = resolveCommunityFromPath(
      path, apiCommunityEntities as any, apiCommunityRelationships as any,
    );
    const curGuid = cur ? String(cur.entity_guid || '').toUpperCase() : null;
    return !!homeGuid && curGuid === homeGuid;
  }, [communitiesLandingRoute, location.pathname, apiCommunityEntities, apiCommunityRelationships]);

  // (DEV-1372: the former currentUserOperationalCommunityGuid memo is gone —
  // CommunitiesPage now reads memberships directly; the declared operating
  // community is available anywhere via getDeclaredCommunityGuid(user,
  // 'operating') in src/Data/store.ts or the type='operating' row in
  // useApiSecurityData().communityMemberships.)

  // ── System admin check moved earlier in component (DEV-1050) so V2-derived
  //    visible-projects/collections memos can reference it. See above.

  // ── Admin services array (shared between ApiStatusModal and AdminApiMonitor) ──
  const adminServices = useMemo<ServiceStatus[]>(() => [
    { name: 'Projects', source: apiLoading ? 'loading' : dataSource as any, count: apiLoading ? null : (apiProjects as any[]).length, loading: apiLoading },
    { name: 'Schedule Series', source: scheduleLoading ? 'loading' : scheduleSource as any, count: scheduleLoading ? null : (apiScheduleData as any[]).length, loading: scheduleLoading },
    { name: 'Finance Series', source: financeLoading ? 'loading' : financeSource as any, count: financeLoading ? null : (apiFinanceData as any[]).length, loading: financeLoading },
    { name: 'KPI Config', source: kpiConfigLoading ? 'loading' : kpiConfigSource === 'none' ? 'ls' : kpiConfigSource as any, count: kpiConfigLoading ? null : apiKpiConfigs.size > 0 ? apiKpiConfigs.size : null, loading: kpiConfigLoading },
    { name: 'Mission Metrics', source: missionLoading ? 'loading' : missionSource as any, count: missionLoading ? null : (apiMissionData as any[]).length, loading: missionLoading },
    { name: 'Mission Targets', source: missionLoading ? 'loading' : missionSource as any, count: missionLoading ? null : Object.keys(apiMissionTargets).length > 0 ? Object.values(apiMissionTargets).reduce((sum, t) => sum + Object.keys(t).length, 0) : null, loading: missionLoading },
    { name: 'Community Metadata', source: communityLoading ? 'loading' : communitySource as any, count: communityLoading ? null : (communityRawCounts.communityEntities + communityRawCounts.relationships + communityRawCounts.collections + communityRawCounts.collectionProjects + communityRawCounts.customEntities + communityRawCounts.schemas + communityRawCounts.schemaFields + communityRawCounts.metadataValues) || null, loading: communityLoading },
    { name: 'Security', source: securityLoading ? 'loading' : securitySource as any, count: securityLoading ? null : (securityRawCounts.users + securityRawCounts.communityMemberships + securityRawCounts.communityRoles + securityRawCounts.projectAcl + securityRawCounts.collectionMemberships + securityRawCounts.securityLinks + securityRawCounts.preferences) || null, loading: securityLoading },
    { name: 'Curve Meta', source: curveMetaLoading ? 'loading' : curveMetaSource as any, count: curveMetaLoading ? null : apiCurveMetaRaw.length || null, loading: curveMetaLoading },
    { name: 'KPI Progress', source: kpiProgressLoading ? 'loading' : kpiProgressSource as any, count: kpiProgressLoading ? null : apiKpiProgressRaw.length || null, loading: kpiProgressLoading },
    { name: 'Workflows', source: workflowsLoading ? 'loading' : workflowsSource as any, count: workflowsLoading ? null : apiWorkflowsRaw.length || null, loading: workflowsLoading },
    { name: 'Media Links', source: mediaStatsLoading ? 'loading' : mediaStatsSource as any, countLabel: mediaStatsLabel, loading: mediaStatsLoading },
  ], [
    apiLoading, dataSource, apiProjects,
    scheduleLoading, scheduleSource, apiScheduleData,
    financeLoading, financeSource, apiFinanceData,
    kpiConfigLoading, kpiConfigSource, apiKpiConfigs,
    missionLoading, missionSource, apiMissionData, apiMissionTargets,
    communityLoading, communitySource, communityRawCounts,
    securityLoading, securitySource, securityRawCounts,
    curveMetaLoading, curveMetaSource, apiCurveMetaRaw,
    kpiProgressLoading, kpiProgressSource, apiKpiProgressRaw,
    workflowsLoading, workflowsSource, apiWorkflowsRaw,
    mediaStatsLoading, mediaStatsSource, mediaStatsLabel,
  ]);

  const adminOnRefresh = useCallback(() => {
    refetchProjects();
    refetchSchedule();
    refetchFinance();
    refetchKpiConfigs();
    refetchMission();
    refetchCommunity();
    refetchSecurity();
    refetchCurveMeta();
    refetchKpiProgress();
    refetchWorkflows();
    refetchMediaStats();
  }, [refetchProjects, refetchSchedule, refetchFinance, refetchKpiConfigs, refetchMission, refetchCommunity, refetchSecurity, refetchCurveMeta, refetchKpiProgress, refetchWorkflows, refetchMediaStats]);

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      {/* DEV-1050 cutover complete — ShadowModeBanner removed along with V1. */}
      {/* ── Public routes rendered OUTSIDE the app shell ── */}
      <RouteGate isSystemAdmin={currentUserIsAdmin} securityLoaded={!securityLoading} adminServices={adminServices} adminOnRefresh={adminOnRefresh} adminUsers={apiSecurityUsers || []} adminCommunityEntities={apiCommunityEntities || []} currentAdminGuid={currentUserGuid} onUsersChanged={refetchSecurity}>

      <div className="l4e-shell">
        <LeftNav
          myImpactRoute={myImpactRoute}
          /* DEV-1458 §7.10 — Home: the resolver target (one resolver, two
             consumers: this button + the "/" post-login landing). */
          homeRoute={communitiesLandingRoute}
          homeActive={homeActive}
          messagesBadge={messagesBadge}
          actionCenterBadge={actionCenterBadge}
          showActionCenter={showActionCenterForAll}
          visibleTiers={navVisibleTiers}
          unlockedFeatures={navFeatures}
          currentUserGuid={currentUserGuid}
        />

        {/* First-login welcome (Skip 2026-07-07) — one screen, no gate. */}
        <WelcomeScreenModal open={showWelcomeScreen} onClose={dismissWelcomeScreen} />

        {/* §7.9.9 — one-time stale Start Page pointer notice. */}
        <StartPageNotice
          userGuid={currentUserGuid}
          stalePrefGuid={landingResolverReady ? startPageResolution.stalePrefGuid : null}
          landedRoute={startPageResolution.route}
          entities={apiCommunityEntities as any}
        />

        {showContributorWelcome && (() => {
          // Ring animation needs the promoted user's face + name (variant E,
          // Skip 2026-07-07). Seed-shaped security row; PromotionRing falls
          // back to initials when no photo exists.
          const cu = (apiSecurityUsers || []).find(
            (u: any) =>
              String(u?.user_guid || '').toUpperCase() ===
              String(currentUserGuid || '').toUpperCase(),
          );
          return (
            <ContributorWelcomeModal
              onClose={dismissContributorWelcome}
              // §7.16 beat 2 — the reveal path: close beat 1 and arrive at
              // the signature page in ONE moment, no reload. The page's own
              // first-visit choreography (radar draw-in + ExplainRadarModal
              // once via the server radar_explained flag) takes it from here.
              onShowMe={() => {
                dismissContributorWelcome();
                navigate('/memberships');
              }}
              displayName={String(cu?.display_name || cu?.name || '').split(/\s+/)[0] || undefined}
              avatarUrl={cu?.profile_image_url || null}
            />
          );
        })()}

        <main className="l4e-main">
          {/* Top bar */}
          <div className="l4e-topbar">
            <div className="l4e-topbar-brand">
              <img src={l4eLogo} alt="" className="l4e-topbar-logo" />
              <span className="l4e-topbar-brandtext"><BrandText entities={apiCommunityEntities} relationships={apiCommunityRelationships} /></span>
            </div>

            <div className="l4e-topbar-title">
              <PageTitle entities={apiCommunityEntities} relationships={apiCommunityRelationships} />
            </div>

            <div className="l4e-topbar-spacer" />

            {/* DEV-1389 — 'bar' mode: persistent field centred between the
                spacers (default). 'icon' mode: the field collapses to a
                magnifier rendered inside the actions group next to the bell
                (see below). Same props in both; only mode + placement differ. */}
            {searchBarMode === 'bar' && (
              <GlobalSearch
                mode="bar"
                projects={apiProjects}
                visibleProjectGuids={visibleProjectGuidSet}
                miniProjects={apiMiniProjects}
                communities={apiCommunityEntities as any}
                customEntities={apiCustomEntities as any}
                relationships={apiCommunityRelationships as any}
                users={apiSecurityUsers}
                currentUserGuid={currentUserGuid}
                communityMemberships={apiCommunityMemberships as any}
                communityRoles={apiCommunityRoles as any}
              />
            )}

            <div className="l4e-topbar-spacer" />

            <div className="l4e-topbar-actions">
              {searchBarMode === 'icon' && (
                <GlobalSearch
                  mode="icon"
                  projects={apiProjects}
                  visibleProjectGuids={visibleProjectGuidSet}
                  miniProjects={apiMiniProjects}
                  communities={apiCommunityEntities as any}
                  customEntities={apiCustomEntities as any}
                  relationships={apiCommunityRelationships as any}
                  users={apiSecurityUsers}
                  currentUserGuid={currentUserGuid}
                  communityMemberships={apiCommunityMemberships as any}
                  communityRoles={apiCommunityRoles as any}
                />
              )}
              {/* Connection paused indicator (idle timeout) */}
              {connectionPaused && (
                <button
                  type="button"
                  className="l4e-paused-indicator"
                  onClick={handleResume}
                  title="Connection paused — click to resume"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                  <span>Paused</span>
                </button>
              )}

              {/* Notification bell */}
              <NotificationBell />

              {/* Chat icon — toggles ChatTray; crossed-out when Chat API is offline */}
              <button
                type="button"
                className={`${chatStyles.chatIconButton}${isChatOffline ? ` ${chatStyles.chatIconOffline}` : ''}`}
                aria-label={isChatOffline ? "Chat unavailable" : "Messages"}
                title={isChatOffline ? "Chat service is offline" : undefined}
                onClick={isChatOffline ? undefined : () => setChatTrayOpen((v) => !v)}
                disabled={isChatOffline}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  {isChatOffline && <line x1="3" y1="3" x2="21" y2="21" stroke="#ef5350" strokeWidth="2.5" />}
                </svg>
                {messagesBadge > 0 && (
                  <span className={chatStyles.chatUnreadDot} />
                )}
              </button>

              {/* User avatar menu — account settings, theme, language, sign out */}
              <AvatarMenu
                displayName={currentUserInfo.displayName}
                profileImg={currentUserInfo.profileImg}
                initials={currentUserInfo.initials}
                isEnabler={currentUserInfo.isEnabler}
              />

              {/* DEV-1457 — App gear is system-admin only. Everything inside it
                  (API status, debug mode, project import, geography admin,
                  passcode, simulated-user switcher) is platform administration;
                  a normal user has no use for any of it. Regular account/theme/
                  language settings live in the AvatarMenu above and stay there.

                  Gated on getIsSystemAdmin() — the AUTHENTICATED user — not on
                  currentUserIsAdmin (the SIMULATED user). Gating on the simulated
                  user would hide the gear the moment an admin simulates a normal
                  user, trapping them with no way to switch back. */}
              {getIsSystemAdmin() && (
              <div className="l4e-appgear" ref={appGearRef}>
                <button
                  type="button"
                  className="l4e-appgear-button"
                  aria-label={t('appGear.settingsLabel')}
                  onClick={() => setAppGearOpen((v) => !v)}
                >
                  <GearIcon />
                </button>

                {appGearOpen && (
                  <div className="l4e-appgear-menu" role="menu" aria-label={t('appGear.settingsLabel')}>
                    <div className="l4e-appgear-section">
                      <div className="l4e-appgear-title">{t('appGear.developer')}</div>
                      <button
                        type="button"
                        className="l4e-appgear-action"
                        onClick={() => { setApiStatusOpen(true); setAppGearOpen(false); }}
                      >
                        {t('appGear.apiStatus')}
                      </button>
                      <label className="l4e-appgear-row">
                        <input type="checkbox" checked={!!debugMode} onChange={(e) => onDebugModeChange(e.target.checked)} />
                        <span>{t('appGear.debugMode')}</span>
                      </label>
                      <button
                        type="button"
                        className="l4e-appgear-action"
                        onClick={() => { setImportProjectsOpen(true); setAppGearOpen(false); }}
                      >
                        {t('appGear.importProjects')}
                      </button>
                    </div>
                    <div className="l4e-appgear-divider" />
                    <div className="l4e-appgear-section">
                      <div className="l4e-appgear-title">{t('appGear.administration')}</div>
                      <button
                        type="button"
                        className="l4e-appgear-action"
                        onClick={() => { navigate('/admin/geo'); setAppGearOpen(false); }}
                      >
                        {t('appGear.manageGeographies')}
                      </button>
                      {currentUserIsAdmin && (
                        <button
                          type="button"
                          className="l4e-appgear-action"
                          onClick={() => { setPasscodeModalOpen(true); setAppGearOpen(false); }}
                        >
                          {t('appGear.changePasscode')}
                        </button>
                      )}
                    </div>
                    {/* DEV-1457 — the simulated-user switcher is gone from the
                        gear. User simulation now lives in the admin console. */}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Content row — Rails (second left column) + page, BELOW the
              topbar so opening/closing a Rail never resizes or shifts the
              full-width app header above it. Each Rail self-gates to its
              section and is mutually exclusive with the other. */}
          <div className="l4e-content-row">
            <CommunitySwitcherRail
              entities={apiCommunityEntities as any}
              relationships={apiCommunityRelationships as any}
              currentUserGuid={currentUserGuid}
              apiCommunityRoles={apiCommunityRoles}
              /* Track 4 Slice C/C′ (§7.3) — role rows + the role reference
                 page read the same engagement entries the badge composer
                 already builds. */
              engagementEntries={actionCenterEngagementEntries}
              /* DEV-1446 (§7.6) — chunk B's reveal predicate drives the
                 signature card vs the locked slot. */
              impactRevealed={showImpactSurfaces}
              impactArriving={signatureArriving}
            />
            <ResourceRegistryRail currentUserGuid={currentUserGuid} />
            {/* DEV-1447 slice 2 — the Discover Communities catalog's geography
                drilldown. Self-gates to /discover/communities. Legal as Discover's
                ONE Rail: on the catalog route the Activity drawer is gone (the
                catalog replaced the globe AND the drawer, §7.1), so this is
                LeftNav + one Rail, not a third column. Selection travels via
                ?geo=<guid> because the Rail and the routed page are siblings
                here, not parent and child. */}
            <GeoTreeRail
              entities={apiCommunityEntities as any}
              relationships={apiCommunityRelationships as any}
            />

          {/* Page content — React Router */}
          <div className="l4e-page">
            <ProjectSelectionProvider>
              <FocusProvider>
                {/* Keyed on pathname so each navigation replays the slide-in (DEV-1330).
                    DEV-1390: collapse /messages/:id → /messages so selecting a
                    different conversation does NOT replay the whole-page slide
                    (which dragged the conversation rail along too). The chat
                    window animates on its own inside MessagesPage. */}
                <div
                  key={location.pathname.startsWith('/messages') ? '/messages' : location.pathname}
                  className="l4e-route-enter"
                >
                <Routes>
                  {/* Post-auth landing. DEV-1448 (§7.6) established the
                      resolver chain: explicit pref → inviting org → home-geo
                      landing walk → browse. DEV-1458 (§7.7 ruling 6) AMENDS
                      what the resolved route RENDERS, not how it resolves: the
                      chain is untouched, but the target is now the community's
                      STANDALONE canonical page — no My Impact rail beside it.
                      Users land IN THE WORLD, in their community; the mirror is
                      one LeftNav click away at /memberships. (This route and
                      the My Impact nav click are no longer "two entry points to
                      one resolver" — the nav click deliberately stopped
                      consulting it. The resolver now has exactly one caller.) */}
                  <Route
                    path="/"
                    element={
                      <FeatureGate feature="community_browse" fallback={<Navigate to="/discover" replace />}>
                        {landingResolverReady
                          ? <Navigate to={communitiesLandingRoute} replace />
                          : null /* data inbound — redirect on next render */}
                      </FeatureGate>
                    }
                  />

                  {/* Projects list */}
                  <Route
                    path="/projects"
                    element={
                      <MyProjectsPage
                        data={Data}
                        dataVersion={projectConfigVersion}
                        kpiMode={kpiMode}
                        onKpiModeChange={setKpiMode}
                        debugMode={debugMode}
                        onDebugModeChange={onDebugModeChange}
                        currentUserGuid={currentUserGuid}
                        onUserChange={onUserChange}
                        authorizedCollectionGuids={authorizedCollectionGuids}
                        apiCollections={apiCollections}
                        apiCollectionProjects={apiCollectionProjects}
                        apiSchemaFields={apiSchemaFields}
                        apiMetadataValues={apiMetadataValues}
                        apiCustomEntities={apiCustomEntities}
                        onProjectConfigChanged={() => setProjectConfigVersion((v) => v + 1)}
                        searchText={projectSearch}
                        onSearchTextChange={setProjectSearch}
                        apiProjectAcl={apiProjectAcl}
                        apiCommunityEntities={apiCommunityEntities as any}
                        apiCommunityRelationships={apiCommunityRelationships as any}
                      />
                    }
                  />

                  {/* Project creation stepper — must come before :id to avoid "new" matching as an id */}
                  <Route
                    path="/projects/new"
                    element={
                      <ProjectStepper
                        currentUserGuid={currentUserGuid}
                        onCreated={() => setProjectConfigVersion((v) => v + 1)}
                        communities={apiCommunityEntities}
                        communityRelationships={apiCommunityRelationships}
                      />
                    }
                  />

                  {/* Project detail — DEV-901 stage 3a: dispatcher decides
                       Mini vs Full based on the GUID's project type. Mini
                       renders MiniProjectDetailPage; Full falls through to
                       the existing ProjectDetailPage. */}
                  <Route
                    path="/projects/:id/:tab?"
                    element={
                      <ProjectDispatcher
                        currentUserGuid={currentUserGuid}
                        debugMode={debugMode}
                        onProjectConfigChanged={() => setProjectConfigVersion((v) => v + 1)}
                        projectData={Data}
                      />
                    }
                  />

                  {/* Standalone workspace detail (personal/household — no community context) */}
                  <Route
                    path="/workspaces/:workspaceId"
                    element={<StandaloneWorkspaceDetailRoute currentUserGuid={currentUserGuid} />}
                  />

                  {/* Communities */}
                  <Route path="/communities" element={<CommunitiesPageRoute entities={apiCommunityEntities} relationships={apiCommunityRelationships} currentUserGuid={currentUserGuid} />} />
                  {/* Slice E′ (§7.2 fold, ratified 2026-07-09): the Lead-tier
                      cards page retired — leadership lives in the Memberships
                      rail's Roles lens (Leadership chip); sysadmin root-org
                      creation relocated to the rail's Create-a-community
                      chooser. Old links/bookmarks land on Memberships. */}
                  <Route path="/my-communities" element={<Navigate to="/communities" replace />} />
                  <Route path="/my-communities/*" element={<Navigate to="/communities" replace />} />
                  <Route
                    path="/communities/:id"
                    element={
                      <CommunityDetailRoute
                        currentUserGuid={currentUserGuid}
                        entities={apiCommunityEntities as any[]}
                        relationships={apiCommunityRelationships as any[]}
                      />
                    }
                  />
                  <Route
                    path="/communities/:id/workspaces"
                    element={<WorkspaceListRoute />}
                  />
                  <Route
                    path="/communities/:id/workspaces/:workspaceId"
                    element={<WorkspaceDetailRoute currentUserGuid={currentUserGuid} />}
                  />
                  {/* DEV-970 — "View all projects" landing for the Alignment tab. */}
                  <Route
                    path="/communities/:id/projects"
                    element={<CommunityProjectsPage />}
                  />

                  {/* Approvals — being restructured to the 4-tab Action Center
                      IA (Approvals · Data · Tasks · Tracking) inside the
                      existing ContentApprovalsPage. DEV-681. The `data` prop
                      carries the same row array the rest of the surfaces
                      consume; the Data tab pulls per-project Schedule /
                      Finance / Mission entry inputs off each row. */}
                  <Route
                    path="/actions"
                    element={
                      <ContentApprovalsPage
                        currentUserGuid={currentUserGuid}
                        debugMode={debugMode}
                        users={apiSecurityUsers}
                        projects={apiProjects}
                        communities={apiCommunityEntities}
                        relationships={apiCommunityRelationships}
                        data={Data}
                        onProjectConfigChanged={() => setProjectConfigVersion((v) => v + 1)}
                        communityMemberships={apiCommunityMemberships}
                        projectAcl={apiProjectAcl}
                        allProjectHeroLookup={allProjectHeroLookup}
                        communityRoleAssignments={apiCommunityRoles as any[] || []}
                      />
                    }
                  />
                  {/* Back-compat: the page was renamed from "Approvals" to the
                      Action Center (DEV-681 / DEV-1368). Old notification links
                      and bookmarks to /approvals redirect to the new /actions. */}
                  <Route path="/approvals" element={<Navigate to="/actions" replace />} />

                  {/* ── MY IMPACT (the /memberships section) ──────────────
                      DEV-1458 (§7.7, Skip ruling 2026-07-12): the section now
                      has a real HOME. Everything under /memberships/* renders
                      with the My Impact rail beside it (isMyImpactSection);
                      NOTHING outside it does. Community pages are standalone,
                      always — see the /communities routes above. */}

                  {/* The section home. Post-reveal the main pane IS the full
                      signature page (§7.7 amends §7.6's "the card is its only
                      front door" — the card stays the only in-section link,
                      but the landing is the page). Pre-reveal Explorers get
                      the belonging overview, no signature element. No
                      FeatureGate here: this route must render for BOTH sides
                      of the reveal, and MembershipsHomePage branches on chunk
                      B's one predicate. */}
                  <Route
                    path="/memberships"
                    element={
                      <MembershipsHomePage
                        data={Data}
                        currentUserGuid={currentUserGuid}
                        debugMode={debugMode}
                        revealed={showImpactSurfaces}
                        entities={apiCommunityEntities as any}
                        relationships={apiCommunityRelationships as any}
                        engagementEntries={actionCenterEngagementEntries}
                      />
                    }
                  />

                  {/* In-section COMMUNITY view — DEV-1458 (§7.7 amended by
                      Skip 2026-07-13). Sibling of /memberships/role/:key: a
                      membership row in the rail opens the community in the
                      MAIN PANE with the rail intact, so switching between your
                      places and organizations is one click — the same grammar
                      roles and projects already use. Consistent UI.

                      Renders CommunityDetailPage DIRECTLY, not
                      CommunityDetailRoute: that wrapper's DEV-947 effect
                      replaces a GUID URL with the canonical slug URL
                      (/rotary/hampton), which would immediately eject the user
                      out of the section to the standalone page. The slug URL is
                      the WORLD's canonical form; this route is the MIRROR's.
                      Same entity, two addresses, each stable under refresh /
                      bookmark / back — which is what buys us entry-aware
                      routing without entry-aware chrome (§7.7 ruling 2 holds:
                      one URL, one chrome). */}
                  <Route
                    path="/memberships/community/:id"
                    element={<InSectionCommunityRoute currentUserGuid={currentUserGuid} />}
                  />

                  {/* Memberships role reference views (Track 4 Slice C′ —
                      architect ratification 2026-07-09, DEV-1425): main-pane
                      role page replacing the MembershipTray. The rail renders
                      beside these (the /memberships prefix). */}
                  <Route
                    path="/memberships/role/:key"
                    element={
                      <MembershipRolePage
                        currentUserGuid={currentUserGuid}
                        engagementEntries={actionCenterEngagementEntries}
                      />
                    }
                  />
                  {/* My-Life4Earth standing view RETIRED (architect
                      ratification 2026-07-09 evening; §7.3/§7.6 amended) —
                      the Impact Signature card + page superseded it. */}
                  <Route
                    path="/memberships/life4earth"
                    element={<Navigate to="/memberships/impact" replace />}
                  />
                  {/* DEV-1446 (§7.6) — the full Impact Signature page INSIDE
                      the My Impact section shell; the rail persists beside it.
                      Reached via the rail's signature card (still the only
                      IN-SECTION link to it).
                      DEV-1458 (§7.7 ruling 4): "/memberships/impact remains
                      valid and simply renders what the index shows post-reveal"
                      — so it renders the SAME component, which branches on
                      chunk B's one reveal predicate. Two consequences, both
                      intended: a pre-reveal deep link degrades to the section
                      home's belonging overview (not, as before, a bounce out to
                      a community page — §7.7 forbids the mirror borrowing the
                      world's pages), and the FeatureGate's impact_radar check
                      is retired here in favour of showImpactSurfaces, the ONE
                      predicate the rail's card already uses. */}
                  <Route
                    path="/memberships/impact"
                    element={
                      <MembershipsHomePage
                        data={Data}
                        currentUserGuid={currentUserGuid}
                        debugMode={debugMode}
                        revealed={showImpactSurfaces}
                        entities={apiCommunityEntities as any}
                        relationships={apiCommunityRelationships as any}
                        engagementEntries={actionCenterEngagementEntries}
                      />
                    }
                  />

                  {/* User Profile */}
                  <Route
                    path="/profile"
                    element={<UserProfilePage currentUserGuid={currentUserGuid} acBadgeCount={actionCenterBadge} />}
                  />
                  <Route
                    path="/profile/:userGuid"
                    element={<UserProfilePage currentUserGuid={currentUserGuid} acBadgeCount={actionCenterBadge} />}
                  />

                  {/* Messages (full-page chat) */}
                  <Route
                    path="/messages"
                    element={<MessagesPage currentUserGuid={currentUserGuid} />}
                  />
                  <Route
                    path="/messages/:conversationId"
                    element={<MessagesPage currentUserGuid={currentUserGuid} />}
                  />

                  {/* Placeholder routes */}
                  <Route path="/discover" element={<DiscoverGlobe projects={effectiveProjects as Record<string, any>[]} communities={apiCommunityEntities as Record<string, any>[]} communityRelationships={apiCommunityRelationships as Record<string, any>[]} isSystemAdmin={currentUserIsAdmin} />} />
                  {/* DEV-1447 — Discover pillar catalogs. Hub-and-spoke: /discover
                      is the globe (the hub); each catalog is its own ROUTE reached
                      from the PillarBar glyphs (the spokes). A catalog carries NO
                      pillar navigation of its own — to switch catalogs the user
                      returns to the globe via browser Back or the LeftNav Discover
                      item, which is why these must be routes and not state inside
                      /discover. Deep-linkability falls out for free (the
                      getting-started `follow_three` row can point straight here).
                      Communities is geo-only (§7.4); Projects / Organizations /
                      Problems / Solutions land as their catalogs are built. */}
                  <Route
                    path="/discover/communities"
                    element={
                      <CommunitiesCatalogPage
                        entities={apiCommunityEntities as any}
                        relationships={apiCommunityRelationships as any}
                        currentUserGuid={currentUserGuid}
                      />
                    }
                  />
                  <Route path="/tv" element={<ComingSoonPage title="L4E.TV" description="Video channel system — another view of existing data." />} />
                  {/* Slice F (DEV-1368 / closes DEV-1115): Engagements Operations
                      merged into the Action Center. /engagements (+ deep links)
                      redirect to /actions; the page is retired. */}
                  <Route path="/engagements" element={<Navigate to="/actions" replace />} />
                  <Route path="/engagements/*" element={<Navigate to="/actions" replace />} />
                  <Route path="/data" element={<ComingSoonPage title="Data" description="Your data obligations across all projects and communities. Enter, review, and track reporting deadlines." />} />
                  <Route
                    path="/my-projects"
                    element={
                      <MyProjectsCardsPage
                        data={Data}
                        currentUserGuid={currentUserGuid}
                        apiProjectAcl={apiProjectAcl}
                        apiCommunityEntities={apiCommunityEntities as any}
                        apiCommunityRelationships={apiCommunityRelationships as any}
                        debugMode={debugMode}
                        onProjectConfigChanged={() => setProjectConfigVersion((v) => v + 1)}
                      />
                    }
                  />
                  {/* ── Content (unified — replaces Chronicles + Knowledge) ── */}
                  <Route path="/content" element={<ContentPage currentUserGuid={currentUserGuid} />} />
                  <Route path="/content/:guid/view" element={<ContentPage currentUserGuid={currentUserGuid} />} />

                  {/* ── Legacy redirects: /chronicles/* → /content ── */}
                  <Route path="/chronicles" element={<Navigate to="/content" replace />} />
                  {/* Creation now happens via CreateChronicleModal on /content. */}
                  <Route path="/chronicles/new" element={<Navigate to="/content" replace />} />
                  {/* Both the bare and /view chronicle URLs render the detail page
                      (the legacy ChronicleEditorPage/ViewPage are retired, DEV-1343). */}
                  <Route path="/chronicles/:guid/view" element={
                    <ChronicleDetailPage
                      currentUserGuid={currentUserGuid}
                      communityEntities={apiCommunityEntities as any[] || []}
                      communityRoleAssignments={apiCommunityRoles as any[] || []}
                      communityEntityRelationships={apiCommunityRelationships as any[] || []}
                    />
                  } />
                  <Route path="/chronicles/:guid" element={
                    <ChronicleDetailPage
                      currentUserGuid={currentUserGuid}
                      communityEntities={apiCommunityEntities as any[] || []}
                      communityRoleAssignments={apiCommunityRoles as any[] || []}
                      communityEntityRelationships={apiCommunityRelationships as any[] || []}
                    />
                  } />

                  {/* ── Legacy redirects: /knowledge/* → /content ── */}
                  <Route path="/knowledge" element={<Navigate to="/content" replace />} />
                  <Route path="/knowledge/:draftId" element={<KnowledgeArticlePage />} />
                  <Route path="/resources" element={<ResourcesPage currentUserGuid={currentUserGuid} />} />
                  {/* DEV-1379 Content Verification Stage 3 — skill-matched Verification window (PULL surface). */}
                  <Route path="/verification" element={<VerificationPage />} />
                  <Route path="/hero-design" element={<HeroDesignExplorer />} />
                  <Route path="/problems" element={<MyProblemsPage currentUserGuid={currentUserGuid} />} />
                  <Route path="/solutions" element={<ComingSoonPage title="Solutions" description="Solution frameworks you lead — approaches, evidence, and replication guidance." />} />
                  <Route
                    path="/portfolio"
                    element={
                      <PortfolioPage
                        data={Data}
                        dataVersion={projectConfigVersion}
                        kpiMode={kpiMode}
                        onKpiModeChange={setKpiMode}
                        debugMode={debugMode}
                        onDebugModeChange={onDebugModeChange}
                        currentUserGuid={currentUserGuid}
                        onUserChange={onUserChange}
                        authorizedCollectionGuids={authorizedCollectionGuids}
                        apiCollections={apiCollections}
                        apiCollectionProjects={apiCollectionProjects}
                        apiSchemaFields={apiSchemaFields}
                        apiMetadataValues={apiMetadataValues}
                        apiCustomEntities={apiCustomEntities}
                        onProjectConfigChanged={() => setProjectConfigVersion((v) => v + 1)}
                        searchText={projectSearch}
                        onSearchTextChange={setProjectSearch}
                        apiProjectAcl={apiProjectAcl}
                        apiCommunityEntities={apiCommunityEntities as any}
                        apiCommunityRelationships={apiCommunityRelationships as any}
                      />
                    }
                  />
                  <Route path="/governance" element={<ComingSoonPage title="Governance" description="Institutional approvals, governance reviews, and quality certification." />} />
                  <Route path="/feed" element={<FeedPage currentUserGuid={currentUserGuid} />} />
                  <Route path="/bookmarks" element={<ComingSoonPage title="Bookmarks" description="Saved pillar items for quick reference." />} />
                  <Route path="/owned-content" element={<ComingSoonPage title="Owned Content" description="Content you have created or own." />} />

                  {/*
                    DEV-888 — Community URL slugs.
                    These routes are LAST so all explicit platform routes
                    above match first. The reserved-word list on the server
                    blocks orgs from claiming top-level slugs that collide
                    with platform routes. The 2-segment route handles
                    /<root_slug>/<sub_slug>; the 1-segment route handles
                    the org-root home (e.g. /rotary → Rotary International).
                  */}
                  {/* DEV-939: variable-depth slug routes. React Router v6 has
                      no optional-segment syntax, so each depth is enumerated.
                      Order matters — most specific first — so a 5-segment URL
                      doesn't match the 1-segment pattern. Five segments covers
                      continent / country / state / city / district; deeper
                      paths (street-level) can be added when needed without
                      affecting any of the above patterns. */}
                  <Route path="/:rootSlug/:subSlug/:s3/:s4/:s5" element={<CommunitySlugRoute currentUserGuid={currentUserGuid} />} />
                  <Route path="/:rootSlug/:subSlug/:s3/:s4" element={<CommunitySlugRoute currentUserGuid={currentUserGuid} />} />
                  <Route path="/:rootSlug/:subSlug/:s3" element={<CommunitySlugRoute currentUserGuid={currentUserGuid} />} />
                  <Route path="/:rootSlug/:subSlug" element={<CommunitySlugRoute currentUserGuid={currentUserGuid} />} />
                  <Route path="/:rootSlug" element={<CommunitySlugRoute currentUserGuid={currentUserGuid} />} />
                </Routes>
                </div>
              </FocusProvider>
            </ProjectSelectionProvider>

            <ImportProjectsModal
              isOpen={importProjectsOpen}
              onClose={() => setImportProjectsOpen(false)}
              onImported={refetchProjects}
            />

            {/* Geography admin moved to /admin/geo */}

            {/* Globe passcode change modal — admin only */}
            {passcodeModalOpen && (
              <PasscodeChangeModal
                onClose={() => setPasscodeModalOpen(false)}
              />
            )}

            <ApiStatusModal
              isOpen={apiStatusOpen}
              onClose={() => setApiStatusOpen(false)}
              apiBaseUrl={API_BASE_URL}
              onRefresh={adminOnRefresh}
              services={adminServices}
            />
          </div>
          </div>
        </main>

        {/* Chat tray — bottom-right floating panel (Tier 2) */}
        <ChatTray
          isOpen={chatTrayOpen}
          onClose={() => setChatTrayOpen(false)}
          currentUserGuid={currentUserGuid}
          startChatWithUserGuid={chatStartWithUserGuid}
          onStartChatWithConsumed={() => setChatStartWithUserGuid(null)}
        />
      </div>
      </RouteGate>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  AppWithAuth — wraps App with AuthProvider + SessionGate            */
/* ------------------------------------------------------------------ */
//
// SessionGate replaces the prior TwoFactorGate. Per audit F1 + DEV-725:
// the gate blocks rendering of the entire app shell — LeftNav, topbar,
// avatar, project content, everything — until the user is fully
// authenticated AND, for users with TotpEnabled=true, has completed the
// TOTP challenge in this session. While a silent refresh is in flight,
// SessionGate renders <AuthSplash />. While requires2FA is true, it
// renders <TotpVerifyScreen />. While unauthenticated on a private path,
// it Navigates to /login preserving the attempted URL in state.from.
//
// ImpersonationBanner, SuspensionOverlay, and AccountSettingsModal sit
// INSIDE SessionGate so they only render for authenticated users — an
// un-authenticated session has no impersonation, no account modal, and
// no suspension overlay to display.

function AppWithAuth() {
  // In development with UseFakeAuth=true, skip real auth.
  const fakeAuth = !!(window as any).__L4E_FAKE_AUTH;

  return (
    <AuthProvider fakeAuth={fakeAuth}>
      <AccountSettingsProvider>
        <SessionGate>
          <ImpersonationBanner />
          <SuspensionOverlay />
          <App />
          <AccountSettingsModal />
        </SessionGate>
      </AccountSettingsProvider>
    </AuthProvider>
  );
}

export default AppWithAuth;
